import * as Api from '@ViewModels';
import { AccountCategory, ETextingFeatureProvider } from '@ViewModels';
import { css, StyleDeclaration } from 'aphrodite';
import produce from 'immer';
import { WritableDraft } from 'immer/dist/types/types-external';
import { computed, makeObservable } from 'mobx';
import { inject, observer, Provider } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidgen } from 'uuid';
import { CoffeeIcon } from '../../../../aida/components/svgs/CoffeeIcon';
import { LeadServedSource } from '../../../../aida/viewModels/queue';
import { IImpersonationContextComponentProps, ILocationState } from '../../../../models';
import { isNorthwesternMutualBrokerDealer } from '../../../utils';
import {
	AdsStageOptions,
	IAdminUserSessionComponentProps,
	IAdsStageOption,
	InternalMeetingNoteType,
	MastheadPortalId,
} from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { LAUNCHDARKLY_SESSION_REPLAY_BASE_URL } from '../../../../models/SessionReplayLogger';
import { convertDurationToString, copyToClipboard, getDefaultDateStringValue } from '../../../../models/UiUtils';
import * as AdminViewModels from '../../../../viewmodels/AdminViewModels';
import { CancellationCategory } from '../../../../viewmodels/AdminViewModels';
import { AutoCompleteSearchField } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../web/components/ConfirmationDialog';
import { DayPicker } from '../../../../web/components/DayPicker';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { PortalDestination } from '../../../../web/components/Portal';
import { ISelectBoxOption, SelectBox } from '../../../../web/components/SelectBox';
import { AnalysisIcon } from '../../../../web/components/svgs/icons/AnalysisIcon';
import { AssessmentReportIcon } from '../../../../web/components/svgs/icons/AssessmentReportIcon';
import { BuildingIcon } from '../../../../web/components/svgs/icons/BuildingIcon';
import { CameraIcon } from '../../../../web/components/svgs/icons/CameraIcon';
import { CancelIcon } from '../../../../web/components/svgs/icons/CancelIcon';
import { ClipboardIcon } from '../../../../web/components/svgs/icons/ClipboardIcon';
import { CompactLogoIcon } from '../../../../web/components/svgs/icons/CompactLogoIcon';
import { DiscountIcon } from '../../../../web/components/svgs/icons/DiscountIcon';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { AccountCompanyNameField } from '../../../components/accountDetails/AccountCompanyNameField';
import { FlagIcon } from '../../../../web/components/svgs/icons/FlagIcon';
import { HIPAAIcon } from '../../../../web/components/svgs/icons/HIPAAIcon';
import { HighlightIcon } from '../../../../web/components/svgs/icons/HighlightIcon';
import { LightningBoltIcon } from '../../../../web/components/svgs/icons/LightningBoltIcon';
import { RefreshIcon } from '../../../../web/components/svgs/icons/RefreshIcon';
import { ScheduleMeetingIcon } from '../../../../web/components/svgs/icons/ScheduleMeetingIcon';
import { SvgIcon } from '../../../../web/components/svgs/icons/SvgIcon';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { UnsubscribeIcon } from '../../../../web/components/svgs/icons/UnsubscribeIcon';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { Input } from '@HoudiniUI/Input';
import { TinyPopover } from '../../../../web/components/TinyPopover';
import { UnsubscribeSignatureHtmlModal } from '../../../../web/components/UnsubscribeSignatureHtmlModal';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../../web/components-v2/Combobox';
import { DateInput } from '../../../../web/components-v2/DateInput';
import { useAppDrawerContext } from '../../../../web/components-v2/Drawer/AppDrawer';
import {
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownSelectTrigger,
	Dropdown as DropdownV2,
} from '../../../../web/components-v2/Dropdown';
import {
	brandPrimary,
	brandPrimaryHover,
	customContentModuleBadge,
	googleLsaModuleBadge,
	googlePpcModuleBadge,
	metaAdsModuleBadge,
	success,
	webModuleBadge,
} from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { AccountStatusIndicator } from '../../../components/AccountStatusIndicator';
import { AssessmentReport } from '../../../components/AssessmentReport';
import { AccountDetailsInfoField } from '../../../components/accountDetails/AccountDetailsInfoField';
import { AccountDetailsNote } from '../../../components/accountDetails/AccountDetailsNote';
import { AdsCampaignsTable } from '../../../components/accountDetails/AdsCampaignsTable';
import { AdsChannelCollapsibleSection } from '../../../components/accountDetails/AdsChannelCollapsibleSection';
import { ConnectedMetaAdAccountField } from '../../../components/accountDetails/ConnectedMetaAdAccountField';
import { HealthScoreCircle } from '../../../components/accountDetails/HealthScoreCircle';
import { MetaConnectionHealthSection } from '../../../components/accountDetails/MetaConnectionHealthSection';
import { isVipAccount, VipBadge } from '../../../components/accountDetails/VipBadge';
import { ModuleTag } from '../../../components/accountDetails/ModuleTag';
import { WebsiteProviderPill } from '../../../components/accountDetails/WebsiteProviderPill';
import { BillingCycleChangeModal } from '../../../components/BillingCycleChangeModal';
import { ConditionalAccess } from '../../../components/ConditionalAccess';
import { ISubmittedValues } from '../../../components/CreateCustomDashboardCard';
import { FindRecordingsModal } from '../../../components/FindRecordingsModal';
import { InfoPopover } from '../../../components/InfoPopover/InfoPopover';
import { InternalOnly } from '../../../components/InternalOnly';
import { ScheduleMeetingModal } from '../../../components/ScheduleMeetingModal';
import { EnhancementType, Privacy, UserRole, WebsiteTier } from '../accountTabs/Settings/options';
import { TCustomSettings } from '../accountTabs/Settings/presentation';
import { AccountDetailsBreadcrumb } from './AccountDetailsBreadcrumb';
import {
	CUSTOM_CALENDAR_DEFAULT_STATUS,
	CUSTOM_CALENDAR_MODIFICATIONS_OPTIONS,
	CUSTOM_CALENDAR_STATUS_OPTIONS,
} from './constants';
import { ImportTemplatesModal } from './ImportTemplatesModal';
import { ResetAccountModal } from './ResetAccount';
import { styleSheet } from './styles';
import { invalidateAccountAdminQuery } from '../../../../queries';

interface IDiscountOption {
	value: Api.AccountDiscountPercent;
	label: string;
}

const DISCOUNT_OPTIONS: IDiscountOption[] = [
	{ value: Api.AccountDiscountPercent.Ten, label: '10%' },
	{ value: Api.AccountDiscountPercent.Fifteen, label: '15%' },
	{ value: Api.AccountDiscountPercent.Twenty, label: '20%' },
	{ value: Api.AccountDiscountPercent.TwentyFive, label: '25%' },
	{ value: Api.AccountDiscountPercent.Thirty, label: '30%' },
	{ value: Api.AccountDiscountPercent.ThirtyFive, label: '35%' },
	{ value: Api.AccountDiscountPercent.Forty, label: '40%' },
	{ value: Api.AccountDiscountPercent.Fifty, label: '50%' },
];

interface IProps
	extends RouteComponentProps<any>,
		IErrorMessageComponentProps,
		IAdminUserSessionComponentProps,
		IEventLoggingComponentProps,
		IFullscreenModalComponentProps {
	showOnlyWebsiteModule?: boolean;
	showOnlyAdsModule?: boolean;
	showOnlyCustomContentModule?: boolean;
	hideHeader?: boolean;
	hideLoadingSpinner?: boolean;
	/** Hides the Edit button in the action entities bar */
	hideEditButton?: boolean;
	/** Controlled edit mode from parent — syncs with internal isEditMode */
	externalEditMode?: boolean;
	/** Callback when edit mode changes internally (Edit click, Save, Cancel) */
	onEditModeChange?: (isEditMode: boolean) => void;
	/** Content rendered inside the context providers (has access to AccountDetailsContext) */
	children?: React.ReactNode;
	/** Callback when the account data is updated (grant access, save, etc.) */
	onAccountUpdated?: (account: Api.IAccount) => void;
	/** Latest account from a sibling instance's save (cross-instance sync) */
	lastSavedAccount?: Api.IAccount;
	/** Extra action nodes rendered alongside the built-in action buttons in the header (e.g. Support Tickets link) */
	extraActionEntities?: React.ReactNode;
}

interface IState {
	account?: Api.IAccount;
	isCustomCalendarEditMode?: boolean;
	context?: AdminViewModels.IAccountDetailsContext;
	/** Comma separated email addresses */
	emailsToUnsubscribe?: string;
	isEditMode?: boolean;
	cancellationMessage?: string;
	cancellationCategory?: CancellationCategory;
	savedAccount?: Api.IAccount;
	selectedDiscount?: Api.AccountDiscountPercent;
	settingsContext?: AdminViewModels.IAccountDetailsSettingsContext;
	showAccountResetModal?: boolean;
	showAssessment?: boolean;
	showCancelAccountModal?: boolean;
	showCancelBusinessAccountNowOrLaterModal?: CancelBusinessAccountNowOrLaterModalBehavior;
	showDiscountModal?: boolean;
	showNextWebMeetingSelector?: boolean;
	showNextAdsMeetingSelector?: boolean;
	showScheduleMeetingModal?: boolean;
	showUnsubscribeContactsModal?: boolean;
	showUnsubscribeSignatureHtmlModal?: boolean;
	showImportTemplatesModal?: boolean;
	showFindRecordingsModal?: boolean;
	showBillingCycleChangeModal?: boolean;
	styles?: StyleDeclaration[];
	editingAdsLiveDateChannel?:
		| InternalMeetingNoteType.AdsFacebook
		| InternalMeetingNoteType.AdsGoogleLsa
		| InternalMeetingNoteType.AdsGooglePpc
		| null;
	adsLiveDateDraft?: string;
	adsLiveDateSaving?: boolean;
	openNextAdsChannelMeetingSelector?:
		| InternalMeetingNoteType.AdsFacebook
		| InternalMeetingNoteType.AdsGoogleLsa
		| InternalMeetingNoteType.AdsGooglePpc
		| null;
}

type CancelBusinessAccountNowOrLaterModalBehavior = {
	now: boolean;
	isOpen: boolean;
};

const PrepareForMeetingMenuItem: React.FC = () => {
	const drawerContext = useAppDrawerContext();
	const handleClick = () => {
		drawerContext.setValue('success-meeting-prep');
	};
	return (
		<DropdownItem onClick={handleClick}>
			<ScheduleMeetingIcon fill='currentColor' />
			Prepare for Meeting
		</DropdownItem>
	);
};

const UnsubscribeConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		representedObject: true,
		title: 'Unsubscribe',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

export const StageOptions: ISelectBoxOption<string>[] = [
	{ title: Api.Stage.Implementation, value: Api.Stage.Implementation },
	{ title: Api.Stage.Success, value: Api.Stage.Success },
	{ title: Api.Stage.Support, value: Api.Stage.Support },
];
export const AccountCategoryOptions: ISelectBoxOption<AccountCategory>[] = [
	{
		value: AccountCategory.None,
		id: 'account-category-option-none',
		title: 'None',
	},
	{
		value: AccountCategory.BrokerDealer,
		id: 'account-category-option-broker-dealer',
		title: 'Broker Dealer',
	},
	{
		value: AccountCategory.Levitate,
		id: 'account-category-option-levitate',
		title: 'Levitate',
	},
	{
		value: AccountCategory.Demo,
		id: 'account-category-option-demo',
		title: 'Demo',
	},
];

const RevertConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: true,
		title: 'Confirm',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const CancelConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: true,
		title: 'Confirm cancellation',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const cancellationReasonOptionsSelect: ISelectBoxOption<CancellationCategory>[] = [
	{ title: 'Never got started', value: CancellationCategory.NeverGotStarted },
	{ title: "Didn't see value", value: CancellationCategory.DidntSeeValue },
	{ title: 'Use case', value: CancellationCategory.UseCase },
	{ title: 'Issues', value: CancellationCategory.Issues },
	{ title: 'Adoption', value: CancellationCategory.Adoption },
	{ title: 'Price', value: CancellationCategory.Price },
	{ title: 'Competitor', value: CancellationCategory.Competitor },
	{ title: 'Concerns', value: CancellationCategory.Concerns },
	{ title: 'Product', value: CancellationCategory.Product },
	{ title: 'Business changes', value: CancellationCategory.BusinessChanges },
].map(({ title, value }) => ({
	value,
	id: `cancellation-reason-option-${title}`,
	title,
}));

export type EnhancementOptionType = 'all' | 'off' | 'contactsOnly' | 'companiesOnly';

interface IAdsChannelDescriptor {
	key: 'facebook' | 'lsa' | 'ppc';
	type:
		| InternalMeetingNoteType.AdsFacebook
		| InternalMeetingNoteType.AdsGoogleLsa
		| InternalMeetingNoteType.AdsGooglePpc;
	label: string;
	enabledKey: 'facebookAds' | 'googleLocalServiceAds' | 'googlePpc';
	enabledDateKey: 'facebookAdsEnabledDate' | 'googleLocalServiceAdsEnabledDate' | 'googlePpcEnabledDate';
	liveDateKey: 'facebookAdsLiveDate' | 'googleLocalServiceAdsLiveDate' | 'googlePpcAdsLiveDate';
	lastMeetingKey: 'lastFacebookAdsMeeting' | 'lastGoogleLocalServiceAdsMeeting' | 'lastGooglePpcAdsMeeting';
	lastStatusKey:
		| 'lastFacebookAdsMeetingStatus'
		| 'lastGoogleLocalServiceAdsMeetingStatus'
		| 'lastGooglePpcAdsMeetingStatus';
	nextMeetingKey: 'nextFacebookAdsMeeting' | 'nextGoogleLocalServiceAdsMeeting' | 'nextGooglePpcAdsMeeting';
	noteIdKey: 'facebookAdsNoteId' | 'googleLocalServiceAdsNoteId' | 'googlePpcAdsNoteId';
	color: string;
}

const AdsChannelDescriptors: IAdsChannelDescriptor[] = [
	{
		key: 'facebook',
		type: InternalMeetingNoteType.AdsFacebook,
		label: 'Meta Ads',
		enabledKey: 'facebookAds',
		enabledDateKey: 'facebookAdsEnabledDate',
		liveDateKey: 'facebookAdsLiveDate',
		lastMeetingKey: 'lastFacebookAdsMeeting',
		lastStatusKey: 'lastFacebookAdsMeetingStatus',
		nextMeetingKey: 'nextFacebookAdsMeeting',
		noteIdKey: 'facebookAdsNoteId',
		color: metaAdsModuleBadge,
	},
	{
		key: 'lsa',
		type: InternalMeetingNoteType.AdsGoogleLsa,
		label: 'Google LSA',
		enabledKey: 'googleLocalServiceAds',
		enabledDateKey: 'googleLocalServiceAdsEnabledDate',
		liveDateKey: 'googleLocalServiceAdsLiveDate',
		lastMeetingKey: 'lastGoogleLocalServiceAdsMeeting',
		lastStatusKey: 'lastGoogleLocalServiceAdsMeetingStatus',
		nextMeetingKey: 'nextGoogleLocalServiceAdsMeeting',
		noteIdKey: 'googleLocalServiceAdsNoteId',
		color: googleLsaModuleBadge,
	},
	{
		key: 'ppc',
		type: InternalMeetingNoteType.AdsGooglePpc,
		label: 'Google PPC',
		enabledKey: 'googlePpc',
		enabledDateKey: 'googlePpcEnabledDate',
		liveDateKey: 'googlePpcAdsLiveDate',
		lastMeetingKey: 'lastGooglePpcAdsMeeting',
		lastStatusKey: 'lastGooglePpcAdsMeetingStatus',
		nextMeetingKey: 'nextGooglePpcAdsMeeting',
		noteIdKey: 'googlePpcAdsNoteId',
		color: googlePpcModuleBadge,
	},
];

class _AccountDetails extends React.Component<IProps, IState> {
	private mAccountOperations: AdminViewModels.AccountOperationsViewModel;
	private mScrollToBottomPortalId: string;
	private mPendingScheduleMeetingModal = false;
	private mImpersonationProviderProps: IImpersonationContextComponentProps = {
		impersonationContext: new AdminViewModels.ImpersonationContextViewModel(),
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<_AccountDetails, 'isLoading'>(this, {
			isLoading: computed,
		});

		const accountId = props.match.params?.accountId;
		this.mAccountOperations = new AdminViewModels.AccountOperationsViewModel(props.userSession, { id: accountId });
		this.state = {
			account: null,
			context: {
				account: null,
				accountOperations: this.mAccountOperations,
			},
			emailsToUnsubscribe: '',
			isEditMode: false,
			isCustomCalendarEditMode: false,
			cancellationMessage: '',
			settingsContext: {
				onAddCustomCard: this.onAddCustomCard,
				onAddCustomField: this.onAddCustomField,
				onAdminSendOnBehalfContactOwnerCriteriaChanged: this.onAdminSendOnBehalfContactOwnerCriteriaChanged,
				onBlogEnabledChanged: this.onBlogEnabledChanged,
				onClassificationEmailsChanged: this.onClassificationEmailsChanged,
				onComplianceEnabledChanged: this.onComplianceEnabledChanged,
				onComplianceEmailAddressChanged: this.onComplianceEmailAddressChanged,
				onComplianceRequiredChanged: this.onComplianceRequiredChanged,
				onSendComplianceReviewResultEmailsChanged: this.onSendComplianceReviewResultEmailsChanged,
				onComplianceWaitTimeChanged: this.onComplianceWaitTimeChanged,
				onConnectionTypesEnabledChanged: this.onConnectionTypesEnabledChanged,
				onCrossAccountTemplateSharingChanged: this.onCrossAccountTemplateSharingChanged,
				onCreateContactsFromCalendarChanged: this.onCreateContactsFromCalendarChanged,
				onCsmViewMyCampaignsEnabledChanged: this.onCsmViewMyCampaignsEnabledChanged,
				onDefaultContactsVisibilityChanged: this.onDefaultContactsVisibilityChanged,
				onDefaultUserRoleChanged: this.onDefaultUserRoleChanged,
				onDefaultEmailStyleEnabledChanged: this.onDefaultEmailStyleEnabledChanged,
				onDefaultEmailStyleFontFaceChanged: this.onDefaultEmailStyleFontFaceChanged,
				onDefaultEmailStyleFontSizeChanged: this.onDefaultEmailStyleFontSizeChanged,
				onDeleteRenewalKeyFactsChanged: this.onDeleteRenewalKeyFactsChanged,
				onDisableMobileAppsChanged: this.onDisableMobileAppsChanged,
				onHideCompaniesChanged: this.onHideCompaniesChanged,
				onHIPAAEnabledChanged: this.onHIPAAEnabledChanged,
				onHideContactCompanyEmployeeReportChanged: this.onHideContactCompanyEmployeeReportChanged,
				onHideEmailCampaignSignatureChanged: this.onHideEmailCampaignSignatureChanged,
				onShowAllContactsByDefaultChanged: this.onShowAllContactsByDefaultChanged,
				onEditHtmlNewsletterFooter: this.onEditHtmlNewsletterFooter,
				onEnableCcInCampaignsChanged: this.onEnableCcInCampaignsChanged,
				onUseSharedMailboxAsDefaultSendFromChanged: this.onUseSharedMailboxAsDefaultSendFromChanged,
				onEnableContentGenerationChanged: this.onEnableContentGenerationChanged,
				onEnableMeetingPrepChanged: this.onEnableMeetingPrepChanged,
				onEnableEmailExecChanged: this.onEnableEmailExecChanged,
				onEnableFileClassificationChanged: this.onEnableFileClassificationChanged,
				onFileClassificationDailyLimitChanged: this.onFileClassificationDailyLimitChanged,
				onAllowEmailContentChanged: this.onAllowEmailContentChanged,
				onAllowIntegrationsChanged: this.onAllowIntegrationsChanged,
				onEnableCustomContentCalendarChanged: this.onEnableCustomContentCalendarChanged,
				onEnablePublicAPIChanged: this.onEnablePublicAPIChanged,
				onEnableStripePaymentsChanged: this.onEnableStripePaymentsChanged,
				onEnableSocialMediaChanged: this.onEnableSocialMediaChanged,
				onEnableSocialMediaLogoOverlayChanged: this.onEnableSocialMediaLogoOverlayChanged,
				onEnableSocialMediaLogoBackgroundChanged: this.onEnableSocialMediaLogoBackgroundChanged,
				onEnableSocialMediaReadOnly: this.onEnableSocialMediaReadOnly,
				onEnableSurveysChanged: this.onEnableSurveysChanged,
				onEnableFilesChanged: this.onEnableFilesChanged,
				onCanSendFileLinksChanged: this.onCanSendFileLinksChanged,
				onEnableTranslationChanged: this.onEnableTranslationChanged,
				onHandwrittenCardsEnabledChanged: this.onHandwrittenCardsEnabledChanged,
				onHandwrittenCardsProviderChanged: this.onHandwrittenCardsProviderChanged,
				onHouseholdsEnabledChanged: this.onHouseholdsEnabledChanged,
				onHouseholdsSendToHouseholdByDefaultChanged: this.onHouseholdsSendToHouseholdByDefaultChanged,
				onHtmlNewsletterChanged: this.onHtmlNewsletterChanged,
				onInternalMeetingsChanged: this.onInternalMeetingsChanged,
				onLookForLeadsChanged: this.onLookForLeadsChanged,
				onEmailEngagementTaggingChanged: this.onEmailEngagementTaggingChanged,
				onMeetingCompanyNameRequiredChanged: this.onMeetingCompanyNameRequiredChanged,
				onMeetingSchedulerEnabled: this.onMeetingSchedulerEnabled,
				onZoomEnabled: this.onZoomEnabled,
				onDataBoardOpportunitiesEnabledChanged: this.onDataBoardOpportunitiesEnabledChanged,
				onDataBoardDonationsEnabledChanged: this.onDataBoardDonationsEnabledChanged,
				onDonationTagSettingsChanged: this.onDonationTagSettingsChanged,
				onDataBoardPoliciesEnabledChanged: this.onDataBoardPoliciesEnabledChanged,
				onDataBoardPoliciesCarrierTagsEnabledChanged: this.onDataBoardPoliciesCarrierTagsEnabledChanged,
				onDataBoardPoliciesLineOfBusinessTagsEnabledChanged: this.onDataBoardPoliciesLineOfBusinessTagsEnabledChanged,
				onDataBoardMattersEnabledChanged: this.onDataBoardMattersEnabledChanged,
				onDataBoardMeetingsEnabledChanged: this.onDataBoardMeetingsEnabledChanged,
				onDataBoardServiceJobsEnabledChanged: this.onDataBoardServiceJobsEnabledChanged,
				onServiceJobTagSettingsChanged: this.onServiceJobTagSettingsChanged,
				onDataBoardVolunteerEnabledChanged: this.onDataBoardVolunteerEnabledChanged,
				onDataBoardMattersMatterStageTagsEnabledChanged: this.onDataBoardMattersMatterStageTagsEnabledChanged,
				onRequiredRoleForFullLevitateUI: this.onRequiredRoleForFullLevitateUI,
				onRequiredRoleToViewAllOpportunities: this.onRequiredRoleToViewAllOpportunities,
				onResetAccountClicked: this.onResetAccountClicked,
				onSaveForAnalysisChanged: this.onSaveForAnalysisChanged,
				onSendEmailForNewLeadChanged: this.onSendEmailForNewLeadChanged,
				onSendOnBehalfEnabled: this.sendOnBehalfEnabled,
				onSendOnBehalfWaitTimeChanged: this.onSendOnBehalfWaitTimeChanged,
				onSentMailClickTrackingChanged: this.onSentMailClickTrackingChanged,
				onSentMailOpenTrackingChanged: this.onSentMailOpenTrackingChanged,
				onSetShowInternalSendOption: this.onSetShowInternalSendOption,
				onSetAllowStartAutomationsOnHold: this.onSetAllowStartAutomationsOnHold,
				onSetAllowAIConditionals: this.onSetAllowAIConditionals,
				onSetEnhancementOption: this.onSetEnhancementOption,
				onSetFinancialReviewOption: this.onSetFinancialReviewOption,
				onSetFinancialReviewOptionChange: this.onSetFinancialReviewOptionChange,
				onSetMaximumGroupAutomationContacts: this.onSetMaximumGroupAutomationContacts,
				onSetMaximumGroupSendContacts: this.onSetMaximumGroupSendContacts,
				onSetMaximumHTMLNewsletterContacts: this.onSetMaximumHTMLNewsletterContacts,
				onSetNoteViewerCanEditEnabledChanged: this.onSetNoteViewerCanEditEnabledChanged,
				onSetRenewalReminderDaysOption: this.onSetRenewalReminderDaysOption,
				onSetRenewalReminderDaysOptionChange: this.onSetRenewalReminderDaysOptionChange,
				onSetRenewalSuppressMonthsOption: this.onSetRenewalSuppressMonthsOption,
				onSetRenewalSuppressMonthsOptionChange: this.onSetRenewalSuppressMonthsOptionChange,
				onSetShowUpcomingBirthdaysToOption: this.onSetShowUpcomingBirthdaysToOption,
				onToggleAccountPreferenceBoolean: this.onToggleAccountPreferenceBoolean,
				onSetSuppressContactsRemovedFromResourceSends: this.onSetSuppressContactsRemovedFromResourceSends,
				onSetSurveyDefaultReviewLink: this.onSetSurveyDefaultReviewLink,
				onSetSurveyNewReviewsEmailNotification: this.onSetSurveyNewReviewsEmailNotification,
				onSetTurning65MonthsOption: this.onSetTurning65MonthsOption,
				onSetTurning65MonthsOptionChange: this.onSetTurning65MonthsOptionChange,
				onSetTurning72MonthsOption: this.onSetTurning72MonthsOption,
				onSetTurning72MonthsOptionChange: this.onSetTurning72MonthsOptionChange,
				onSetTurning73MonthsOption: this.onSetTurning73MonthsOption,
				onSetTurning73MonthsOptionChange: this.onSetTurning73MonthsOptionChange,
				onSetNewDonationsOptionChange: this.onSetNewDonationsOptionChange,
				onSetNewPolicyOptionChange: this.onSetNewPolicyOptionChange,
				onSetNewMatterOptionChange: this.onSetNewMatterOptionChange,
				onSetMatterStageChangedOptionChange: this.onSetMatterStageChangedOptionChange,
				onSetMatterStatusChangedOptionChange: this.onSetMatterStatusChangedOptionChange,
				onSetServiceJobCompletedOptionChange: this.onSetServiceJobCompletedOptionChange,
				onSetServiceJobCompletedMinimumTotal: this.onSetServiceJobCompletedMinimumTotal,
				onSetTurningXXMonthsOption: this.onSetTurningXXMonthsOption,
				onSetTurningXXMonthsOptionChange: this.onSetTurningXXMonthsOptionChange,
				onSetTurningXX2MonthsOption: this.onSetTurningXX2MonthsOption,
				onSetTurningXX2MonthsOptionChange: this.onSetTurningXX2MonthsOptionChange,
				onSetTurningXX3MonthsOption: this.onSetTurningXX3MonthsOption,
				onSetTurningXX3MonthsOptionChange: this.onSetTurningXX3MonthsOptionChange,
				onSetTurningXX4MonthsOption: this.onSetTurningXX4MonthsOption,
				onSetTurningXX4MonthsOptionChange: this.onSetTurningXX4MonthsOptionChange,
				onSetTurningXX5MonthsOption: this.onSetTurningXX5MonthsOption,
				onSetTurningXX5MonthsOptionChange: this.onSetTurningXX5MonthsOptionChange,
				onSuppressCustomCardOptionChange: this.onSuppressCustomCardOptionChange,
				onKeepInTouchAgentChanged: this.onKeepInTouchAgentChanged,
				onKeepInTouchAgentDailyScanQuotaChanged: this.onKeepInTouchAgentDailyScanQuotaChanged,
				onAIEmailCampaignChanged: this.onAIEmailCampaignChanged,
				onAIEmailCampaignQuotaChanged: this.onAIEmailCampaignQuotaChanged,
				onKeepInTouchAgentMinDaysBetweenScansChanged: this.onKeepInTouchAgentMinDaysBetweenScansChanged,
				onTextingChanged: this.onTextingChanged,
				onTextingDailySendLimitChanged: this.onTextingDailySendLimitChanged,
				onTextingHourlySendLimitChanged: this.onTextingHourlySendLimitChanged,
				onTextingProviderChanged: this.onTextingProviderChanged,
				onTextingSecondsBetweenSendsChanged: this.onTextingSecondsBetweenSendsChanged,
				onTextingRequireArchivingChanged: this.onTextingRequireArchivingChanged,
				onTextingZoomPhoneEnabledChanged: this.onTextingZoomPhoneEnabledChanged,
				onTextingUseParentCampaignRegistrationChanged: this.onTextingUseParentCampaignRegistrationChanged,
				onTimelineChanged: this.onTimelineChanged,
				onTurningXXInputChanged: this.onTurningXXInputChanged,
				onTurningXX2InputChanged: this.onTurningXX2InputChanged,
				onTurningXX3InputChanged: this.onTurningXX3InputChanged,
				onTurningXX4InputChanged: this.onTurningXX4InputChanged,
				onTurningXX5InputChanged: this.onTurningXX5InputChanged,
				onTwoFactorAuthChanged: this.onTwoFactorAuthChanged,
				onAllowOAuthLoginChanged: this.onAllowOAuthLoginChanged,
				onOnlyAllowOAuthLoginChanged: this.onOnlyAllowOAuthLoginChanged,
				onEnabledOAuthProvidersChanged: this.onEnabledOAuthProvidersChanged,
				onWebsitesEnablementChanged: this.onWebsitesEnablementChanged,
				onWebsitesDudaSiteIdChanged: (value: string) => this.onWebsitesStringValueChanged(value, 'dudaSiteId'),
				onWebsitesWebsiteUrlChanged: (value: string) => this.onWebsitesStringValueChanged(value, 'websiteUrl'),
				onUpdateCustomCard: this.onUpdateCustomCard,
				onUpdateCustomCardDays: this.onUpdateCustomCardDays,
				setStartOnBehalfTriggerEnabled: this.setStartOnBehalfTriggerEnabled,
				setTagAutomationTriggerEnabled: (enabled: boolean) =>
					this.setAutomationFeatureEnabledStatus(
						Api.AutomationTriggerType.Tag,
						'additionalAllowedTextStepTriggers',
						enabled
					),
				setManualAutomationTriggerEnabled: (enabled: boolean) =>
					this.setAutomationFeatureEnabledStatus(
						Api.AutomationTriggerType.Manual,
						'additionalAllowedTextStepTriggers',
						enabled
					),
				setTextingCampaignAutomationTriggerEnabled: this.setTextingCampaignAutomationTriggerEnabled,
				toggleAutomations: this.toggleAutomations,
				onUpdatePreApprovedFeature: this.onUpdatePreApprovedFeature,
				onUpdatePreApprovedDisplayText: this.onUpdatePreApprovedDisplayText,
				onUpdatePreApprovedEmailPrefix: this.onUpdatePreApprovedEmailPrefix,
				onUpdatePreReviewedFeature: this.onUpdatePreReviewedFeature,
				onUpdatePreReviewedDisplayText: this.onUpdatePreReviewedDisplayText,
				onUpdatePreReviewedEmailPrefix: this.onUpdatePreReviewedEmailPrefix,
				onUpdateRejectedFeature: this.onUpdateRejectedFeature,
				onUpdateRejectedEmailPrefix: this.onUpdateRejectedEmailPrefix,
				onUpdateRejectedDisplayText: this.onUpdateRejectedDisplayText,
			},
			showAssessment: false,
			showCancelAccountModal: false,
			showDiscountModal: false,
			showScheduleMeetingModal: false,
			showImportTemplatesModal: false,
		};
		this.mScrollToBottomPortalId = uuidgen();
	}

	public componentDidMount() {
		this.loadAccount();

		// Check for openScheduleMeeting query param (from payment confirmation page)
		// Store flag to open modal after account loads (handled in componentDidUpdate)
		const searchParams = new URLSearchParams(this.props.location.search);
		if (searchParams.get('openScheduleMeeting') === 'true') {
			this.mPendingScheduleMeetingModal = true;
			// Clear the query param to prevent re-triggering on refresh
			searchParams.delete('openScheduleMeeting');
			const newSearch = searchParams.toString();
			this.props.history.replace({
				pathname: this.props.location.pathname,
				search: newSearch ? `?${newSearch}` : '',
			});
		}
	}

	public componentDidUpdate(prevProps: IProps, prevState: IState) {
		// Open schedule meeting modal once account is loaded
		if (this.mPendingScheduleMeetingModal && this.state.account && !prevState.account) {
			this.mPendingScheduleMeetingModal = false;
			this.setState({ showScheduleMeetingModal: true });
		}

		// Sync saved account from sibling instance (cross-instance sync).
		// Apply state directly instead of calling applyAccount to avoid re-triggering
		// onAccountUpdated → setLastSavedAccount → infinite loop via new object references.
		const lastSaved = this.props.lastSavedAccount;
		const hasSiblingUpdate = lastSaved && lastSaved !== prevProps.lastSavedAccount;
		if (hasSiblingUpdate) {
			const clonedAccount = JSON.parse(JSON.stringify(lastSaved)) as Api.IAccount;
			this.updateEditableAccount(clonedAccount, {
				isEditMode: false,
				savedAccount: clonedAccount,
				settingsContext: { ...this.state.settingsContext },
			});
		}

		// Sync edit mode from external prop
		if (this.props.externalEditMode !== prevProps.externalEditMode) {
			if (this.props.externalEditMode && !this.state.isEditMode) {
				this.enterEditMode();
			} else if (!this.props.externalEditMode && this.state.isEditMode && !hasSiblingUpdate) {
				// !hasSiblingUpdate: when a sibling save triggers this cycle, updateEditableAccount
				// above already sets isEditMode: false — skip exitEditMode to avoid stale-state revert.
				this.exitEditMode();
			}
		}
	}

	/** Whether the account is cancelled. Drives the cancelled pills and the red status indicator. */
	private get isCancelled(): boolean {
		return this.mAccountOperations.accountStatus === Api.ActivationStatus.CANCELED;
	}

	public render() {
		const {
			context,
			settingsContext,
			showAssessment,
			account,
			isEditMode,
			showDiscountModal,
			showCancelBusinessAccountNowOrLaterModal,
			selectedDiscount,
			cancellationMessage,
			showScheduleMeetingModal,
			showAccountResetModal,
		} = this.state;
		const { match } = this.props;

		const isCancelled = this.isCancelled;
		const hideMeetingWarning =
			account?.additionalInfo?.customerSuccessName || account?.additionalInfo?.implementationMeeting;
		const isVip = isVipAccount(account);
		const isPastDue = account?.planDetails?.billingState?.toLowerCase() === 'past_due';
		const billingType = account?.planDetails?.billingType;
		const isComp = billingType === Api.BillingType.Comp;
		const isTrial = billingType === Api.BillingType.Trial;

		return (
			<Provider {...this.mImpersonationProviderProps}>
				<AdminViewModels.AccountDetailsContext.Provider value={context}>
					<AdminViewModels.AccountDetailsSettingsContext.Provider value={settingsContext}>
						<div className='account-details'>
							{!this.props.hideHeader && (
								<MultiContainerHeader
									appBarHeader={
										<div className={css(styleSheet.breadcrumbsContainer)}>
											<AccountDetailsBreadcrumb accountId={match.params.accountId!} />
											{!!account && isVip && <VipBadge />}
											{!!account && isComp && <span className={css(styleSheet.billingTypeBadge)}>Comp</span>}
											{!!account && isTrial && <span className={css(styleSheet.billingTypeBadge)}>Free Trial</span>}
											{!!account && isPastDue && <span className={css(styleSheet.implementationBadge)}>Past Due</span>}
											{!hideMeetingWarning && (
												<span className={css(styleSheet.implementationBadge)}>Implementation Not Scheduled</span>
											)}
										</div>
									}
									appBarPortalDestinationId={MastheadPortalId}
								/>
							)}
							<div>
								{!this.props.hideHeader && (
									<div className={`${css(styleSheet.companyName)}`}>
										<div className={`${css(styleSheet.statusColorContainer)}`}>
											<AccountStatusIndicator account={account} isCancelled={isCancelled} />
											{account?.features?.hipaa?.enabled && (
												<HIPAAIcon width={32} height={32} className={css(styleSheet.hipaaIcon)} />
											)}
											<div className={css(styleSheet.companyNameColumn)}>
												<AccountCompanyNameField
													account={account}
													nameClassName={css(styleSheet.accountName)}
													inputStyles={[styleSheet.companyNameInput]}
													onNameClick={this.goToLevitateCompany}
													onSaved={this.props.onAccountUpdated}
												/>
												<button className={css(styleSheet.copyLinkButton)} onClick={this.copyLink}>
													Copy link to clipboard
												</button>
											</div>
											<HealthScoreCircle
												health={(this.mAccountOperations.account || account)?.additionalInfo?.health}
											/>
										</div>
										{this.renderActionEntities()}
										<Modal
											isOpen={showDiscountModal}
											onRequestClose={this.onDiscountModalRequestClose}
											useDefaultHeader={true}
										>
											<div className={css(styleSheet.discountTitle)}>Apply Account Discount</div>
											<div className={css(styleSheet.discountComboboxContainer)}>
												<Combobox
													items={DISCOUNT_OPTIONS}
													value={DISCOUNT_OPTIONS.find(opt => opt.value === selectedDiscount) ?? null}
													onValueChange={newValue => this.onDiscountOptionChange(newValue as IDiscountOption | null)}
													itemToStringLabel={opt => (opt as IDiscountOption).label}
												>
													<ComboboxInput placeholder='Select discount...' />
													<ComboboxContent>
														<ComboboxList>
															{(option: IDiscountOption) => (
																<ComboboxItem key={option.value} value={option}>
																	<ComboboxItemRadioIndicator />
																	{option.label}
																</ComboboxItem>
															)}
														</ComboboxList>
													</ComboboxContent>
												</Combobox>
											</div>
											<div className={css(baseStyleSheet.flex)}>
												<button
													onClick={this.onDiscountModalRequestCloseFunc(selectedDiscount, false)}
													className={css(baseStyleSheet.ctaButton, styleSheet.applyButton)}
												>
													Apply
												</button>
												<button
													onClick={this.onDiscountModalRequestCloseFunc(null, true)}
													className={css(baseStyleSheet.ctaButtonReverse)}
												>
													Cancel
												</button>
											</div>
										</Modal>
										<BillingCycleChangeModal
											isOpen={!!this.state.showBillingCycleChangeModal}
											accountId={account?.id ?? ''}
											billingReferenceId={account?.planDetails?.billingReferenceId}
											onClose={() => this.setState({ showBillingCycleChangeModal: false })}
										/>
										<ConfirmationDialog
											className={css(styleSheet.cancelAccountModal)}
											icon={<WarningIcon />}
											modalProps={{
												isOpen: showCancelBusinessAccountNowOrLaterModal?.isOpen,
												onRequestClose: (result, canceled) =>
													this.onCancelBusinessAccount(showCancelBusinessAccountNowOrLaterModal?.now, result, canceled),
											}}
											options={CancelConfirmationOptions}
											title={
												showCancelBusinessAccountNowOrLaterModal?.now
													? `Are you sure you want to cancel ${account?.companyName} immediately?`
													: `Because ${account?.companyName} is renewing ${getDefaultDateStringValue(
															account?.planDetails?.renewalDate
														)}, this account will be added to the pending cancellation list. Are you sure you want to request to cancel this account?`
											}
										>
											<SelectBox
												options={cancellationReasonOptionsSelect}
												onSelectionChanged={this.setCancellationCategory}
												selectedOption={this.getCancellationCategory()}
												contentClassName={css(styleSheet.selectContent)}
												placeholder='Select Cancellation Category'
												triggerClassName={css(styleSheet.selectCancellation)}
											/>
											<Input
												styles={[styleSheet.companyNameInput]}
												id='cancel-account-now-reason'
												onChange={this.onCancellationMessageChanged}
												type='text'
												placeholder='Cancellation Message'
												value={cancellationMessage}
											/>
											{this.isStillInContract() && (
												<div className={css(styleSheet.contractWarning)}>
													Hey, slow your roll. This account may still be in their contract and you&apos;re trying to
													cancel it early.
												</div>
											)}
										</ConfirmationDialog>
									</div>
								)}
								{this.renderAccountDetails()}
								{!!this.mAccountOperations.account && (
									<AssessmentReport
										accountId={this.mAccountOperations.account.id}
										isOpen={showAssessment}
										onRequestClose={this.assessmentModalClose}
									/>
								)}
							</div>
							{isEditMode && this.hasEditableContent && (
								<div className={css(styleSheet.editModeBar)}>
									<span className='cta-button-small' onClick={this.saveAccount}>
										Save
									</span>
									<span style={{ marginLeft: 10 }} className='cta-button-reverse-small' onClick={this.onCancelEdit}>
										Cancel
									</span>
								</div>
							)}
							{this.isLoading && !this.props.hideLoadingSpinner && (
								<LoadingSpinner className={css(styleSheet.loading)} type='large' />
							)}
							<PortalDestination id={this.mScrollToBottomPortalId} />
							<ScheduleMeetingModal
								account={account}
								userSession={this.props.userSession}
								modalProps={{
									isOpen: showScheduleMeetingModal,
									onRequestClose: this.onScheduleMeetingRequestClose,
								}}
							/>
							<ConditionalAccess permission={AdminViewModels.AdminPermission.ResetAccount}>
								{showAccountResetModal ? (
									<ResetAccountModal
										showAccountResetModal={showAccountResetModal}
										onAccountResetModalRequestClose={this.onAccountResetModalRequestClose}
										companyName={account?.companyName}
									/>
								) : null}
							</ConditionalAccess>
						</div>
						<UnsubscribeSignatureHtmlModal
							address={account?.planDetails?.address}
							features={account?.features}
							isFinancial={account?.additionalInfo?.industry === Api.Industry.Financial}
							modalProps={{
								isOpen: this.state.showUnsubscribeSignatureHtmlModal,
								onRequestClose: this.onUnsubscribeSignatureModalHtmlRequestClose,
							}}
							impersonationContext={this.mImpersonationProviderProps.impersonationContext}
							onDomainReleased={this.loadAccount}
						/>
						{!!account && this.props.children}
					</AdminViewModels.AccountDetailsSettingsContext.Provider>
				</AdminViewModels.AccountDetailsContext.Provider>
			</Provider>
		);
	}

	private onCancellationMessageChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ cancellationMessage: e.target.value });
	};

	private onDiscountOptionChange = (option: IDiscountOption | null) => {
		this.setState({ selectedDiscount: option?.value });
	};

	private setCancellationCategory = (select: ISelectBoxOption<CancellationCategory>) => {
		this.setState({ cancellationCategory: select.value });
	};

	private getCancellationCategory = () =>
		cancellationReasonOptionsSelect.find(reason => reason.value === this.state.cancellationCategory);

	/**
	 * Helper method to update only the custom calendar feature in account state
	 * without triggering unnecessary email configuration reloads
	 */
	private updateCustomCalendarFeature = (result: Api.IOperationResult<Api.IAccount>): void => {
		if (result?.value) {
			const updatedAccount = { ...this.state.account };
			if (updatedAccount.features) {
				updatedAccount.features.customContentCalendar = result.value.features?.customContentCalendar;
				this.setState({ account: updatedAccount });
			}
		}
	};

	private get hasEditableContent() {
		const { showOnlyWebsiteModule, showOnlyAdsModule, showOnlyCustomContentModule } = this.props;
		return !!showOnlyWebsiteModule || !!showOnlyAdsModule || !!showOnlyCustomContentModule;
	}

	private get isLoading() {
		return (
			!this.mAccountOperations.isLoaded || !!this.mAccountOperations.isLoading || !!this.mAccountOperations.isSaving
		);
	}

	private goToLevitateCompany = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();

		this.getInternalCompanyLink()
			?.then(result => {
				const url = import.meta.env.VITE_LINKS_URL + `/companies/${result.value}`;
				window.open(url);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				const { errorMessages, logApiError } = this.props;
				errorMessages.pushApiError(err);
				logApiError('LoadInternalCompanyIdError', err);
			});
	};

	private getInternalCompanyLink = async (): Promise<Api.IOperationResult<string>> => {
		const res = await this.mAccountOperations.loadInternalCompanyId();
		return res;
	};

	private copyLink = () => {
		const { location, history } = this.props;
		let linkToAccount = history.createHref(location);
		if (linkToAccount.startsWith('#')) {
			linkToAccount = window.location.href;
		}
		const done = copyToClipboard(linkToAccount);
		if (done) {
			toast.success('Copied account link to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard... ctrl+click the link to get the URL');
		}
	};

	private navigateToCoffee = () => {
		this.getInternalCompanyLink()
			?.then(result => {
				const url = `${import.meta.env.VITE_COFFEE_URL}#/queue?from=${LeadServedSource.SharedLink}&companyId=${result.value}`;
				window.open(url);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				const { errorMessages, logApiError } = this.props;
				errorMessages.pushApiError(err);
				logApiError('LoadInternalCompanyIdError', err);
			});
	};

	private copyAccountId = () => {
		const done = copyToClipboard(this.mAccountOperations?.account?.id);
		if (done) {
			toast.success('Copied account ID to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard...');
		}
	};

	private updateBilling = () => {
		this.mAccountOperations
			.updateBilling()
			?.then(() => {
				toast.success('Billing information synced with Chargify.');
			})
			?.catch(err => {
				const { errorMessages, logApiError } = this.props;
				errorMessages.pushApiError(err);
				logApiError('UpdateBillingStatus-Error', err);
			});
	};

	private isStillInContract = () => {
		const { account } = this.state;
		const planDetails = account?.planDetails;
		if (!planDetails?.billingReferenceId || planDetails.billingReferenceId <= 0) {
			return false;
		}
		if (!planDetails.billingCycle || planDetails.billingCycle >= 12) {
			return false;
		}
		const creationDate = account?.creationDate;
		const renewalDate = planDetails?.renewalDate;
		if (!creationDate || !renewalDate) {
			return false;
		}
		const oneYearAfterCreation = moment(creationDate).add(1, 'year');
		return moment(renewalDate).isBefore(oneYearAfterCreation);
	};

	private cancelAccount = () => {
		const { account } = this.state;
		const now = moment(account?.planDetails?.renewalDate).isSame(moment(), 'month');
		this.setState({ showCancelBusinessAccountNowOrLaterModal: { isOpen: true, now } });
	};

	private onCancelBusinessAccount = (now: boolean, result?: IConfirmationDialogOption, canceled?: boolean) => {
		const { account, cancellationMessage, cancellationCategory } = this.state;

		if (!canceled && result.isDestructive) {
			if (!cancellationMessage || cancellationCategory === null) {
				toast.error('You must provide a reason for cancelling this account.');
				return;
			}
			const cancellationReasonRequest = {
				cancellationMessage,
				cancellationCategory,
			};
			if (now) {
				this.mAccountOperations
					.cancelImmediately(cancellationReasonRequest)
					?.then(() => {
						toast.success(`The ${account.companyName} account has been cancelled.`);
					})
					?.catch(err => {
						const { errorMessages, logApiError } = this.props;
						errorMessages.pushApiError(err);
						logApiError('CancelImmediately-Error', err);
					});
			} else {
				this.mAccountOperations
					.cancelLater(cancellationReasonRequest)
					?.then(() => {
						toast.success(`Request to cancel the ${account.companyName} account has been sent.`);
					})
					?.catch(err => {
						const { errorMessages, logApiError } = this.props;
						errorMessages.pushApiError(err);
						logApiError('CancelLater-Error', err);
					});
			}
		}

		this.setState({
			showCancelBusinessAccountNowOrLaterModal: { isOpen: false, now: undefined },
		});
	};

	private onApplyDiscount = () => this.setState({ showDiscountModal: true });

	private onDiscountModalRequestClose = (result?: Api.AccountDiscountPercent, canceled?: boolean) => {
		const { errorMessages, logApiError } = this.props;
		if (result && !canceled) {
			const loadingToastId = toast.loading('Applying discount...');
			this.mAccountOperations
				.discount(result)
				?.then(updatedAccount => {
					toast.dismiss(loadingToastId);
					toast.success('Discount successfully applied');
					if (updatedAccount) {
						this.applyAccount(updatedAccount);
					}
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					toast.dismiss(loadingToastId);
					toast.error(err?.systemMessage || 'Failed to apply discount');
					errorMessages.pushApiError(err);
					logApiError('DiscountAccount-Error', err);
				});
		}
		this.setState({ showDiscountModal: false });
	};

	private onDiscountModalRequestCloseFunc = (result?: Api.AccountDiscountPercent, cancelled?: boolean) => () => {
		this.onDiscountModalRequestClose(result, cancelled);
	};

	private renderActionEntities() {
		const { account, isEditMode, showCancelAccountModal } = this.state;

		return (
			<div className={`${css(styleSheet.actionEntitiesBar)}`}>
				<InternalOnly>
					{!isEditMode && (
						<button className={`${css(styleSheet.actionEntities)}`} onClick={this.onScheduleMeeting}>
							<ScheduleMeetingIcon fill='currentColor' />
							<span style={{ marginLeft: 10 }}>Schedule Meeting</span>
						</button>
					)}
				</InternalOnly>
				{!isEditMode && !!this.mAccountOperations.allowLevitateImpersonationUntil && (
					<>
						<span className={css(styleSheet.actionEntitiesReadOnly)}>
							<CompactLogoIcon className={css(styleSheet.iconLevitateAccess)} />
							<span style={{ marginLeft: 10 }}>Access Granted</span>
						</span>
						<InfoPopover
							message={`Access granted until: ${moment(this.mAccountOperations.allowLevitateImpersonationUntil).format('MM/DD/YYYY @ h:mm a')}`}
							side='bottom'
						/>
					</>
				)}
				<ConditionalAccess permission={AdminViewModels.AdminPermission.GrantLoginOnBehalf}>
					{!isEditMode && !this.mAccountOperations.allowLevitateImpersonationUntil && (
						<button className={`${css(styleSheet.actionEntities)}`} onClick={this.grantTemporaryAccess}>
							<CompactLogoIcon className={css(styleSheet.iconLevitateAccess)} />
							<span style={{ marginLeft: 10 }}>Grant Access</span>
						</button>
					)}
				</ConditionalAccess>
				<InternalOnly>
					<button className={`${css(styleSheet.actionEntities)}`} onClick={this.navigateToCoffee}>
						<CoffeeIcon className={css(styleSheet.coffeeLogo)} />
						<span style={{ marginLeft: 10 }}>View in Coffee</span>
					</button>
				</InternalOnly>
				<ConditionalAccess
					permission={AdminViewModels.AdminPermission.AssignSuccessManager}
					or={AdminViewModels.AdminPermission.ModifyAccount}
				>
					{!isEditMode && !this.props.hideEditButton && (
						<button className={`${css(styleSheet.actionEntities)}`} onClick={this.accountEditClick}>
							<EditItemPenIcon />
							<span style={{ marginLeft: 10 }}>Edit</span>
						</button>
					)}
				</ConditionalAccess>
				{!isEditMode && this.props.extraActionEntities}
				<DropdownV2>
					<DropdownEllipsisTrigger styles={[styleSheet.actionEntities]} />
					<DropdownContent align='end' styles={[styleSheet.moreMenu]}>
						<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount}>
							{!isEditMode && <PrepareForMeetingMenuItem />}
						</ConditionalAccess>
						<ConditionalAccess
							permission={AdminViewModels.AdminPermission.AssignSuccessManager}
							or={AdminViewModels.AdminPermission.ModifyAccount}
						>
							{!isEditMode && (
								<DropdownItem onClick={this.onClickAccountFlagged}>
									<FlagIcon isAccount={true} flagged={this.mAccountOperations.accountFlagged} />
									{this.mAccountOperations.accountFlagged ? 'Unflag Account' : 'Flag Account'}
								</DropdownItem>
							)}
						</ConditionalAccess>
						<InternalOnly>
							<DropdownItem onClick={this.launchQuickSight}>
								<AssessmentReportIcon />
								<span>QuickSight Active Use</span>
							</DropdownItem>
						</InternalOnly>
						<InternalOnly>
							<DropdownItem onClick={this.showUnsubscribeContactsModal}>
								<UnsubscribeIcon />
								<span>Add to Unsubscribe List</span>
							</DropdownItem>
						</InternalOnly>
						<InternalOnly>
							{!!account && !!account.id && (
								<DropdownItem onClick={this.openSessionReplays}>
									<HighlightIcon width={12} height={12} />
									<span>View Session Replays</span>
								</DropdownItem>
							)}
						</InternalOnly>
						<InternalOnly>
							{!!account && !!account.id && (
								<DropdownItem onClick={this.onFindRecordingsClick}>
									<CameraIcon />
									<span>Find Recordings</span>
								</DropdownItem>
							)}
						</InternalOnly>
						<InternalOnly>
							<DropdownItem onClick={this.generateAssessmentClick}>
								<AssessmentReportIcon />
								<span>Generate Assessment Report</span>
							</DropdownItem>
						</InternalOnly>
						<ConditionalAccess
							permission={AdminViewModels.AdminPermission.CreateAndDeleteSnapshots}
							elseChildren={null}
						>
							<DropdownItem onClick={this.takeSnapshot}>
								<AnalysisIcon fillColor={success} />
								<span>Take Snapshot for {moment().format('MMMM YYYY')}</span>
							</DropdownItem>
						</ConditionalAccess>
						<ConditionalAccess permission={AdminViewModels.AdminPermission.OnlyForDevelopers} elseChildren={null}>
							<DropdownItem onClick={this.copyAccountId}>
								<ClipboardIcon className={css(styleSheet.clipboardIcon)} fillColor={brandPrimaryHover} />
								<span>Copy Account ID</span>
							</DropdownItem>
						</ConditionalAccess>
						<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
							<DropdownItem onClick={this.onImportTemplates}>
								<SvgIcon height={16} width={16}>
									<g fill='none' fillRule='evenodd'>
										<path
											fill={brandPrimaryHover}
											d='M2 0h8l4 4v10c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2C0 .9.9 0 2 0zm6 7H2v1h6V7zm4 3H2v1h10v-1zm0 2H2v1h10v-1zM8 0v4h4l-4-4z'
										/>
									</g>
								</SvgIcon>
								<span>Import Templates</span>
							</DropdownItem>
						</ConditionalAccess>
						{/* Cancel trial accounts */}
						{this.mAccountOperations.accountStatus !== Api.ActivationStatus.CANCELED &&
							this.mAccountOperations.isCancelable && (
								<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
									<DropdownItem onClick={this.onCancelImmediately}>
										<TrashIcon className={css(styleSheet.centerTrashIcon)} />
										<span>Cancel Trial Immediately</span>
									</DropdownItem>
								</ConditionalAccess>
							)}
						{/* Apply Discount */}
						{this.mAccountOperations.isPaid && !!account?.planDetails?.billingReferenceId && (
							<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
								<DropdownItem onClick={this.onApplyDiscount}>
									<DiscountIcon className={css(styleSheet.centerDiscountIcon)} />
									<span>Apply Discount</span>
								</DropdownItem>
							</ConditionalAccess>
						)}
						{/* Upgrade Account */}
						<InternalOnly>
							{this.mAccountOperations.isPaid && !!account?.planDetails?.billingReferenceId && (
								<DropdownItem
									onClick={() => {
										this.props.history.push(`/tools/upgrade-wizard/${this.state.account?.id}`);
									}}
								>
									<LightningBoltIcon className={css(styleSheet.centerDiscountIcon)} fillColor={brandPrimary} />
									<span>Upgrade Account</span>
								</DropdownItem>
							)}
						</InternalOnly>
						{/* Change Billing Cycle */}
						{this.mAccountOperations.isPaid && !!account?.planDetails?.billingReferenceId && (
							<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
								<DropdownItem
									onClick={() => {
										this.setState({ showBillingCycleChangeModal: true });
									}}
								>
									<RefreshIcon className={css(styleSheet.centerDiscountIcon)} fillColor={brandPrimary} />
									<span>Change Billing Cycle</span>
								</DropdownItem>
							</ConditionalAccess>
						)}
						{/* Cancel business accounts */}
						{this.mAccountOperations.isPaid && (
							<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount} elseChildren={null}>
								<DropdownItem onClick={this.cancelAccount}>
									<CancelIcon className={css(styleSheet.centerDiscountIcon)} />
									<span>Cancel Account</span>
								</DropdownItem>
							</ConditionalAccess>
						)}
						{/* Update billing for testing purposes */}
						{this.mAccountOperations.isPaid && (
							<ConditionalAccess
								permission={AdminViewModels.AdminPermission.OnlyForDevelopers}
								or={AdminViewModels.AdminPermission.SupportLevel3}
								elseChildren={null}
							>
								<DropdownItem onClick={this.updateBilling}>
									<BuildingIcon
										className={css(styleSheet.centerDiscountIcon)}
										stroke={brandPrimary}
										strokeWidth='1.5'
									/>
									<span>Update Billing Status</span>
								</DropdownItem>
							</ConditionalAccess>
						)}
					</DropdownContent>
				</DropdownV2>
				<ConfirmationDialog
					className={css(styleSheet.cancelAccountModal)}
					icon={<WarningIcon />}
					modalProps={{
						isOpen: showCancelAccountModal,
						onRequestClose: this.onCancelImmediatelyRequestClose,
					}}
					options={RevertConfirmationOptions}
					title='Are you sure you want to cancel this account? All of the data associated with this account will be deleted.'
				/>
				{this.state.showImportTemplatesModal && account && (
					<ImportTemplatesModal
						isOpen={this.state.showImportTemplatesModal}
						onRequestClose={this.onImportTemplatesModalClose}
						destinationAccountId={account.id}
						accountName={account.companyName}
					/>
				)}
				{this.state.showFindRecordingsModal && account && (
					<FindRecordingsModal
						isOpen={this.state.showFindRecordingsModal}
						onClose={this.onFindRecordingsModalClose}
						accountId={account.id}
						accountName={account.companyName}
						account={account}
					/>
				)}
			</div>
		);
	}

	private onCancelImmediatelyRequestClose = (result: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (result?.isDestructive && !cancelled) {
			const { errorMessages } = this.props;
			const { account } = this.state;

			const promise = this.mAccountOperations.deactivateAccount(account);

			if (promise) {
				promise
					.then(value => {
						toast.success(`Account canceled successfully`);

						this.applyAccount(value);
					})
					.catch((error: Api.IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
					});

				this.setState({
					showCancelAccountModal: false,
				});
			}
		}

		if (result?.isCancel || cancelled) {
			this.setState({
				showCancelAccountModal: false,
			});
		}
	};

	private onCancelImmediately = () => {
		this.setState({ showCancelAccountModal: true });
	};

	private takeSnapshot = () => {
		this.mAccountOperations
			.takeSnapshot()
			?.then(() => {
				toast.success(`Snapshot created in the ${moment().format('MMMM YYYY')} report`);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				const { errorMessages } = this.props;
				errorMessages.pushApiError(err);
			});
	};

	private openSessionReplays = () => {
		if (!this.state.account?.id) return;
		const query = encodeURIComponent(`accountId=${this.state.account.id}`);
		const url = `${LAUNCHDARKLY_SESSION_REPLAY_BASE_URL}?query=${query}`;
		window.open(url);
	};

	private renderAccountDetails() {
		if (!!this.mAccountOperations.isLoading || !this.mAccountOperations.isLoaded) {
			return null;
		}

		const { userSession, showOnlyWebsiteModule, showOnlyAdsModule, showOnlyCustomContentModule } = this.props;
		const { isEditMode } = this.state;
		const account = this.mAccountOperations.account || this.state.account;
		const isModifyingAccount = isEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount);
		const showWebsiteSection = account?.features?.websites?.enabled || account?.features?.websites?.cancelledDate;
		const showAdsSection = this.hasAdsComponents(account);
		const showBlogSection = account?.features?.blogFeature?.enabled;
		const showCustomCalendarSection = account?.features?.customContentCalendar?.enabled;

		if (showOnlyWebsiteModule) {
			return (
				<div>
					<InternalOnly>
						<div className={css(styleSheet.rightSection)}>
							{showWebsiteSection ? (
								<div
									className={`${css(styleSheet.overviewContainer)} ${css(styleSheet.detailsColumns)} ${css(styleSheet.websiteOverviewContainer)}`}
								>
									<div className={css(styleSheet.moduleTagRow)}>
										<ModuleTag text='Web' color={webModuleBadge} />
									</div>
									{!!this.mAccountOperations.isLoaded && (
										<>
											<div className={`${css(styleSheet.overviewContainerColumn)}`}>
												{this.renderWebsiteFirstColumn()}
												{showBlogSection && this.renderBlogInfo()}
											</div>

											{this.renderWebsiteSecondColumn(isModifyingAccount)}
											{this.renderWebsiteThirdColumn()}
											{this.renderWebsiteFourthColumn()}
										</>
									)}
									<AccountDetailsNote
										type={InternalMeetingNoteType.Websites}
										accountOperations={this.mAccountOperations}
									/>
								</div>
							) : null}
						</div>
					</InternalOnly>
				</div>
			);
		}

		if (showOnlyAdsModule) {
			return (
				<div>
					<InternalOnly>
						<div className={css(styleSheet.rightSection)}>
							{showAdsSection ? this.renderAdsSection(isModifyingAccount, isEditMode) : null}
						</div>
					</InternalOnly>
				</div>
			);
		}

		if (showOnlyCustomContentModule) {
			return (
				<div>
					<InternalOnly>
						<div className={css(styleSheet.rightSection)}>
							{showCustomCalendarSection ? (
								<div
									className={`${css(styleSheet.overviewContainer)} ${css(styleSheet.customCalendarOverviewContainer)}`}
								>
									<div className={css(styleSheet.moduleTagRow)}>
										<ModuleTag text='Custom Content' color={customContentModuleBadge} />
									</div>
									{!!this.mAccountOperations.isLoaded && (
										<>
											<div style={{ flex: 1, display: 'flex' }}>
												{this.renderCustomCalendarFirstColumn()}
												{this.renderCustomCalendarSecondColumn()}
											</div>
											{this.renderCustomCalendarEditButton()}
										</>
									)}
								</div>
							) : null}
						</div>
					</InternalOnly>
				</div>
			);
		}

		return (
			<div>
				<ConfirmationDialog
					modalProps={{
						isOpen: !!this.state.showUnsubscribeContactsModal,
						onRequestClose: this.onUnsubscribeConfirmationRequestClose,
					}}
					options={UnsubscribeConfirmationOptions}
					title='Add to Unsubscribe List'
				>
					<input
						className={css(styleSheet.textBoxFill)}
						id='emails-to-unsubscribe'
						onChange={this.onEmailsToUnsubscribeChanged}
						value={this.state.emailsToUnsubscribe || ''}
						placeholder='Emails to unsubscribe, separated by comma'
					/>
				</ConfirmationDialog>
			</div>
		);
	}

	private onEmailsToUnsubscribeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ emailsToUnsubscribe: e.target.value });
	};

	private showUnsubscribeContactsModal = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		this.setState({ showUnsubscribeContactsModal: true });
	};

	private onUnsubscribeConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const { emailsToUnsubscribe } = this.state;
		const { logApiError, errorMessages } = this.props;
		if (!canceled && result.representedObject) {
			const emails = emailsToUnsubscribe.split(',').map(x => x.trim());
			this.mAccountOperations
				.unsubscribeContactsByEmail(emails)
				.then(res => {
					emails.forEach(eml => {
						if (!res.succeeded.includes(eml)) {
							toast.error(`Unable to unsubscribe: ${eml}`);
						}
					});
					if (res.succeeded.length) {
						toast.success(`Unsubscribed emails: ${res.succeeded.join(', ')}`);
					}
				})
				.catch(err => {
					errorMessages.pushApiError(err);
					logApiError('UnsubscribeContactsFromAccount-Error', err);
				});
		}
		this.setState({
			emailsToUnsubscribe: '',
			showUnsubscribeContactsModal: false,
		});
	};

	private launchQuickSight = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		const { fullscreenModal, userSession } = this.props;
		const viewModel = new AdminViewModels.QuickSightDashboardViewModel(
			userSession,
			AdminViewModels.QuickSightDashboardViewModel.ActiveUseId,
			this.mAccountOperations.account.id
		);
		const locationState: ILocationState<AdminViewModels.QuickSightDashboardViewModel, any> = { viewModel };
		fullscreenModal.history.push('/quicksight/dashboard', locationState);
	};

	private grantTemporaryAccess = () => {
		const { errorMessages, logApiError } = this.props;
		return this.mAccountOperations
			.grantTemporaryAccess()
			?.then(value => {
				const until = moment(value.allowLevitateImpersonationUntil).format('MM/DD/YYYY @ h:mm a');
				this.applyAccount(value);
				toast.success(`Access has been granted until: ${until}`);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('GrantTemporaryAccess-Error', err);
			});
	};

	private onScheduleMeeting = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		this.setState({
			showScheduleMeetingModal: true,
		});
	};

	private onScheduleMeetingRequestClose = () => {
		this.setState({
			showScheduleMeetingModal: false,
		});
	};

	private getWebsiteUrl = () => {
		const { account } = this.state;

		if (account?.features?.websites?.websiteUrl === undefined) {
			return {
				websiteUrl: null,
				hostName: null,
			};
		}

		// Add https:// if no protocol is defined
		const websiteUrl = !account.features.websites.websiteUrl
			? null
			: account.features.websites.websiteUrl.startsWith('http://') ||
					account.features.websites.websiteUrl.startsWith('https://')
				? account.features.websites.websiteUrl
				: `https://${account.features.websites.websiteUrl}`;

		const hostName = () => {
			if (!account.features.websites.websiteUrl) {
				return null;
			}
			try {
				const url = new URL(websiteUrl);
				return url.hostname;
			} catch {
				return account.features.websites.websiteUrl;
			}
		};

		return {
			websiteUrl,
			hostName: hostName(),
		};
	};

	private renderWebsiteFirstColumn() {
		const { account, isEditMode } = this.state;

		if (!account) {
			return null;
		}

		const website = this.getWebsiteUrl();

		const lastWebsiteMeetingNoShow = account.additionalInfo.lastWebsiteMeetingStatus === 'No showed';
		const isWebsiteCancelled = account?.features?.websites?.cancelledDate;
		const websiteAlertStatus = !isWebsiteCancelled && lastWebsiteMeetingNoShow ? 'no show' : null;

		const websiteStage = isWebsiteCancelled ? 'Cancelled' : (account.additionalInfo.websiteStage ?? '-');

		return (
			<>
				<AccountDetailsInfoField label='Website Stage / Status' isEditMode={isEditMode}>
					<>
						<span>{websiteStage}</span>
						{websiteAlertStatus && (
							<>
								&nbsp;<span className={css(styleSheet.websiteStageAlert)}>({websiteAlertStatus})</span>
							</>
						)}
					</>
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Website URL' isEditMode={isEditMode}>
					{isWebsiteCancelled ? null : website.hostName ? (
						<a
							className={css(baseStyleSheet.brandLink, styleSheet.websiteLink)}
							href={website.websiteUrl}
							target='_blank'
							rel='noreferrer'
						>
							{website.hostName}
						</a>
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Built With' isEditMode={isEditMode}>
					{this.renderWebsiteProviderPill()}
				</AccountDetailsInfoField>
			</>
		);
	}

	private renderWebsiteProviderPill() {
		return <WebsiteProviderPill websites={this.state.account?.features?.websites} />;
	}

	private renderBlogInfo() {
		const { account, isEditMode } = this.state;
		return (
			<AccountDetailsInfoField label='Blog URL' isEditMode={isEditMode}>
				{account.features?.blogFeature?.domain ? (
					<a
						className={css(baseStyleSheet.brandLink, styleSheet.websiteLink)}
						href={account.features?.blogFeature?.domain || null}
						target='_blank'
						rel='noreferrer'
					>
						{account.features.blogFeature.domain}
					</a>
				) : (
					'-'
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderWebsiteThirdColumn() {
		const { account, isEditMode } = this.state;

		if (!account) {
			return null;
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Kickoff Date' isEditMode={isEditMode}>
					{account.additionalInfo.websiteKickoffDate
						? getDefaultDateStringValue(account.additionalInfo.websiteKickoffDate)
						: '-'}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Launch Date' isEditMode={isEditMode}>
					{account.additionalInfo.websiteLaunchDate
						? getDefaultDateStringValue(account.additionalInfo.websiteLaunchDate)
						: '-'}
				</AccountDetailsInfoField>
				{account.features?.websites?.cancelledDate ? (
					<AccountDetailsInfoField label='Cancelled Date' isEditMode={isEditMode}>
						{getDefaultDateStringValue(account.features.websites.cancelledDate)}
					</AccountDetailsInfoField>
				) : null}
			</div>
		);
	}

	private renderWebsiteSecondColumn(isModifyingAccount: boolean) {
		const { account, isEditMode } = this.state;

		if (!account) {
			return null;
		}

		const statsUrl = account.features?.websites?.dudaSiteId
			? `https://my.duda.co/home/dashboard/stats/sites/${account.features.websites.dudaSiteId}`
			: null;

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Web Specialist' isEditMode={isEditMode}>
					{isModifyingAccount
						? this.renderWebSpecialistAutocomplete()
						: this.mAccountOperations.webSpecialistDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Website Stats' isEditMode={isEditMode}>
					{statsUrl ? (
						<a
							className={css(baseStyleSheet.brandLink, styleSheet.websiteLink)}
							href={statsUrl}
							target='_blank'
							rel='noreferrer'
						>
							View in Duda
						</a>
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
			</div>
		);
	}

	private renderCustomCalendarFirstColumn() {
		const { account, isCustomCalendarEditMode } = this.state;
		const { userSession } = this.props;

		if (!account) {
			return null;
		}

		const isModifyingAccount =
			isCustomCalendarEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount);
		const showEstimatedDeliveryDate = account.features?.customContentCalendar?.status === 'In Progress';

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Custom Calendar Status' isEditMode={isCustomCalendarEditMode}>
					{isModifyingAccount ? (
						this.renderCustomCalendarStatusDropdown()
					) : (
						<span
							style={{
								color: this.getCustomCalendarStatusColor(account.features?.customContentCalendar?.status),
								fontWeight: 500,
							}}
						>
							{account.features?.customContentCalendar?.status || '-'}
						</span>
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Next Intake Form Due' isEditMode={isCustomCalendarEditMode}>
					{isModifyingAccount ? (
						<DateInput value={this.getIntakeFormDueDateValue()} onChange={this.onCustomCalendarIntakeFormDueChanged} />
					) : account.features?.customContentCalendar?.nextIntakeFormDue ? (
						moment(account.features.customContentCalendar.nextIntakeFormDue).format('M/D/YY')
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
				{showEstimatedDeliveryDate && (
					<AccountDetailsInfoField label='Estimated Delivery Date' isEditMode={isCustomCalendarEditMode}>
						{isModifyingAccount ? (
							<DateInput value={this.getEstimatedDeliveryDateValue()} onChange={this.onEstimatedDeliveryDateChanged} />
						) : account.features?.customContentCalendar?.estimatedDeliveryDate ? (
							moment(account.features.customContentCalendar.estimatedDeliveryDate).format('M/D/YY')
						) : (
							'-'
						)}
					</AccountDetailsInfoField>
				)}
			</div>
		);
	}

	private renderCustomCalendarSecondColumn() {
		const { account, isCustomCalendarEditMode } = this.state;
		const { userSession } = this.props;

		if (!account) {
			return null;
		}

		const isModifyingAccount =
			isCustomCalendarEditMode && userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount);

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Custom Calendar Modifications' isEditMode={isCustomCalendarEditMode}>
					{isModifyingAccount
						? this.renderCustomCalendarModificationsDropdown()
						: account.features?.customContentCalendar?.modifications || '-'}
				</AccountDetailsInfoField>
				{account.features?.customContentCalendar?.statusLastUpdated && (
					<AccountDetailsInfoField label='Status Last Updated' isEditMode={isCustomCalendarEditMode}>
						{getDefaultDateStringValue(account.features.customContentCalendar.statusLastUpdated)}
					</AccountDetailsInfoField>
				)}
			</div>
		);
	}

	private renderCustomCalendarEditButton() {
		const { isCustomCalendarEditMode } = this.state;
		const { userSession } = this.props;

		if (!userSession.hasPermission(AdminViewModels.AdminPermission.ModifyAccount)) {
			return null;
		}

		return (
			<div>
				{isCustomCalendarEditMode ? (
					<button onClick={this.onCustomCalendarEditToggle} className={css(baseStyleSheet.ctaButtonSmall)}>
						Done
					</button>
				) : (
					<button
						onClick={this.onCustomCalendarEditToggle}
						className={css(baseStyleSheet.brandLink)}
						style={{ display: 'flex', alignItems: 'center' }}
					>
						<EditItemPenIcon className={css(styleSheet.editIcon)} />
						<span style={{ marginLeft: 5 }}>Edit</span>
					</button>
				)}
			</div>
		);
	}

	private getIntakeFormDueDateValue(): string {
		const dateString = this.state.account?.features?.customContentCalendar?.nextIntakeFormDue;
		if (!dateString) return '';
		const date = moment(dateString);
		return date.isValid() ? date.format('YYYY-MM-DD') : '';
	}

	private getEstimatedDeliveryDateValue(): string {
		const dateString = this.state.account?.features?.customContentCalendar?.estimatedDeliveryDate;
		if (!dateString) return '';
		const date = moment(dateString);
		return date.isValid() ? date.format('YYYY-MM-DD') : '';
	}

	private getCustomCalendarStatusColor(status: string | undefined): string {
		switch (status) {
			case 'Intake Form Needed':
				return 'maroon';
			case 'In Progress':
				return 'purple';
			case 'Delivered':
				return 'green';
			case 'Waiting on Next Intake':
				return 'darkgray';
			default:
				return 'inherit';
		}
	}

	private renderCustomCalendarStatusDropdown() {
		const { account } = this.state;
		const currentValue = account?.features?.customContentCalendar?.status || CUSTOM_CALENDAR_DEFAULT_STATUS;

		return (
			<DropdownV2>
				<DropdownSelectTrigger styles={[styleSheet.customCalendarSelectBox]}>{currentValue}</DropdownSelectTrigger>
				<DropdownContent>
					{CUSTOM_CALENDAR_STATUS_OPTIONS.map(option => (
						<DropdownItem key={option.value} onClick={() => this.onCustomCalendarStatusChanged(option)}>
							{option.title}
						</DropdownItem>
					))}
				</DropdownContent>
			</DropdownV2>
		);
	}

	private renderCustomCalendarModificationsDropdown() {
		const { account } = this.state;
		const currentValue =
			account?.features?.customContentCalendar?.modifications || CUSTOM_CALENDAR_MODIFICATIONS_OPTIONS[0].value;

		return (
			<DropdownV2>
				<DropdownSelectTrigger styles={[styleSheet.customCalendarSelectBox]}>{currentValue}</DropdownSelectTrigger>
				<DropdownContent>
					{CUSTOM_CALENDAR_MODIFICATIONS_OPTIONS.map(option => (
						<DropdownItem key={option.value} onClick={() => this.onCustomCalendarModificationsChanged(option)}>
							{option.title}
						</DropdownItem>
					))}
				</DropdownContent>
			</DropdownV2>
		);
	}

	private renderWebsiteFourthColumn() {
		const { account, isEditMode, showNextWebMeetingSelector } = this.state;

		if (!account) {
			return null;
		}

		return (
			<div className={`${css(styleSheet.overviewContainerColumn)}`}>
				<AccountDetailsInfoField label='Last Web Meeting' isEditMode={isEditMode}>
					{account.additionalInfo.lastWebsiteMeeting
						? getDefaultDateStringValue(account.additionalInfo.lastWebsiteMeeting)
						: '-'}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField
					label='Next Web Meeting'
					isEditMode={isEditMode}
					info='You can click the date to edit the next meeting!'
				>
					<TinyPopover
						anchor={
							<span onClick={this.setNextWebMeetingSelectorOpen(true)} className={css(styleSheet.nextMeetingValue)}>
								{this.mAccountOperations.nextWebsitesMeeting}
							</span>
						}
						isOpen={showNextWebMeetingSelector}
						dismissOnOutsideAction={true}
						onRequestClose={this.setNextWebMeetingSelectorOpen(false)}
						align='center'
						placement={['top', 'bottom', 'left', 'right']}
					>
						<div className={css(styleSheet.dayPickerContainer)}>
							<DayPicker
								allowPastDates={false}
								className='date-range-picker-calendar-day-picker'
								onDayClick={this.onNextWebMeetingDaySelected}
							/>
						</div>
					</TinyPopover>
				</AccountDetailsInfoField>
			</div>
		);
	}

	private setNextWebMeetingSelectorOpen = (open: boolean) => () => {
		this.setState({ showNextWebMeetingSelector: open });
	};

	private onNextWebMeetingDaySelected = (nextMeeting: Date) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.updateNextWebsitesMeeting(nextMeeting)?.catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
			logApiError('UpdateNextMeeting-Error', err);
		});
	};

	private getEnabledAdsChannels(): IAdsChannelDescriptor[] {
		const account = this.mAccountOperations.account;
		const ads = account?.features?.ads;
		const all = AdsChannelDescriptors;
		return all.filter(c => !!ads?.[c.enabledKey]);
	}

	private getAdsLiveStatus(): { label: string; variant: 'positive' | 'negative' } {
		const account = this.mAccountOperations.account;
		const enabledChannels = this.getEnabledAdsChannels();
		if (enabledChannels.length > 0 && account?.additionalInfo?.adsLiveDate) {
			return { label: 'Live', variant: 'positive' };
		}
		return { label: 'Not Live', variant: 'negative' };
	}

	private getChannelLiveStatus(channel: IAdsChannelDescriptor): { label: string; variant: 'positive' | 'negative' } {
		const liveDate = this.mAccountOperations.account?.additionalInfo?.[channel.liveDateKey];
		return liveDate ? { label: 'Live', variant: 'positive' } : { label: 'Not Live', variant: 'negative' };
	}

	private renderAdsLiveStatusPill(status: { label: string; variant: 'positive' | 'negative' }) {
		const variantStyle =
			status.variant === 'positive' ? styleSheet.adsLiveStatusTagPositive : styleSheet.adsLiveStatusTagNegative;
		return <span className={css(styleSheet.adsLiveStatusTag, variantStyle)}>{status.label}</span>;
	}

	private renderAdsBoxEditButton(isEditMode: boolean) {
		if (isEditMode) return null;
		return (
			<ConditionalAccess permission={AdminViewModels.AdminPermission.ModifyAccount}>
				<button className={css(styleSheet.adsSectionEditButton)} onClick={this.accountEditClick}>
					<EditItemPenIcon />
					<span className={css(styleSheet.adsSectionEditLabel)}>Edit</span>
				</button>
			</ConditionalAccess>
		);
	}

	private renderAdsSection = (isModifyingAccount: boolean, isEditMode: boolean) => {
		const isLoaded = !!this.mAccountOperations.isLoaded;
		const enabledChannels = isLoaded ? this.getEnabledAdsChannels() : [];
		const isSingleChannel = enabledChannels.length === 1;
		const isMultiChannel = enabledChannels.length > 1;
		return (
			<div className={css(styleSheet.adsSectionStack)}>
				{/* Single-channel folds the general-info header into the channel box itself
				    (Jes's unified-box feel). Multi-channel and zero-channel get a dedicated
				    "Advertising Status" box up top. Each section is a bordered box with a text
				    heading (no ModuleTag pill). */}
				{!isSingleChannel && (
					<div className={css(styleSheet.adsSectionBox)}>
						{this.renderAdsSectionHeader(isModifyingAccount, isEditMode)}
						{isLoaded && (
							<>
								{this.renderAdsCrossCuttingFields({ isModifyingAccount, isEditMode })}
								{this.renderLegacyAdsNote()}
								{enabledChannels.length === 0 ? (
									<div className={css(styleSheet.adsEmptyChannelsMessage)}>
										No ads channels enabled on this account. Enable Meta Ads, Google LSA, or Google PPC via the pricing
										wizard to track per-channel progress.
									</div>
								) : null}
							</>
						)}
					</div>
				)}
				{isLoaded &&
					enabledChannels.map(channel =>
						this.renderAdsChannelSection(channel, {
							isCollapsible: isMultiChannel,
							includeCrossCutting: isSingleChannel,
							isModifyingAccount,
							isEditMode,
						})
					)}
			</div>
		);
	};

	private renderAdsSectionHeader = (isModifyingAccount: boolean, isEditMode: boolean) => {
		const isLoaded = !!this.mAccountOperations.isLoaded;
		return (
			<div className={css(styleSheet.adsSectionHeader)}>
				<h2 className={css(styleSheet.adsSectionTitle)}>General Information</h2>
				{isLoaded && this.renderAdsStageTag(isModifyingAccount)}
				{isLoaded && this.renderAdsLiveStatusPill(this.getAdsLiveStatus())}
				{isLoaded && this.renderAdsBoxEditButton(isEditMode)}
			</div>
		);
	};

	private renderAdsStageTag = (isModifyingAccount: boolean) => {
		const serverAccount = this.mAccountOperations.account;
		if (!serverAccount || !this.state.account) return null;
		const lastAdsMeetingNoShow = serverAccount.additionalInfo?.lastAdsMeetingStatus === 'No showed';
		const adsAlertStatus = lastAdsMeetingNoShow ? 'no show' : null;
		const adsStage = serverAccount.additionalInfo?.adsStage ?? 'None';

		if (isModifyingAccount) {
			return (
				<div className={css(styleSheet.adsStageCombobox)}>
					<Combobox
						items={AdsStageOptions}
						value={
							AdsStageOptions.find(opt => opt.value === (this.state.account?.additionalInfo?.adsStage ?? 'None')) ??
							null
						}
						onValueChange={newValue => this.onAdsStageChanged(newValue as IAdsStageOption | null)}
						itemToStringLabel={opt => (opt as IAdsStageOption).label}
					>
						<ComboboxInput placeholder='Select stage...' />
						<ComboboxContent>
							<ComboboxList>
								{(option: IAdsStageOption) => (
									<ComboboxItem key={option.value} value={option}>
										<ComboboxItemRadioIndicator />
										{option.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
			);
		}

		return (
			<span className={css(styleSheet.adsStageTag)}>
				{adsStage}
				{adsAlertStatus && <span className={css(styleSheet.adsStageTagAlert)}> ({adsAlertStatus})</span>}
			</span>
		);
	};

	private renderAdsChannelSection = (
		channel: IAdsChannelDescriptor,
		opts: {
			isCollapsible: boolean;
			includeCrossCutting: boolean;
			isModifyingAccount: boolean;
			isEditMode: boolean;
		}
	) => {
		const { isCollapsible, includeCrossCutting, isModifyingAccount, isEditMode } = opts;
		const account = this.mAccountOperations.account;
		const additionalInfo = account?.additionalInfo;
		const lastMeeting = additionalInfo?.[channel.lastMeetingKey];
		const lastStatus = additionalInfo?.[channel.lastStatusKey];
		const enabledDate = account?.features?.ads?.[channel.enabledDateKey];
		// In single-channel mode the channel header replaces the "Advertising Status"
		// outer header, so it carries the Stage tag + overall live pill + edit button.
		// In multi-channel mode it just shows the channel-specific live pill.
		const statusChip = includeCrossCutting ? (
			<>
				{this.renderAdsStageTag(isModifyingAccount)}
				{this.renderAdsLiveStatusPill(this.getAdsLiveStatus())}
				{this.renderAdsBoxEditButton(isEditMode)}
			</>
		) : (
			this.renderAdsLiveStatusPill(this.getChannelLiveStatus(channel))
		);
		return (
			<div key={channel.key} className={css(styleSheet.adsSectionBox)}>
				<AdsChannelCollapsibleSection label={channel.label} statusChip={statusChip} isCollapsible={isCollapsible}>
					{includeCrossCutting ? (
						<>
							{this.renderAdsCrossCuttingFields({
								isModifyingAccount,
								isEditMode,
								channelForLiveDate: channel,
								extraGridFields:
									channel.key === 'facebook' && account ? (
										<ConnectedMetaAdAccountField impersonationContext={{ account }} />
									) : null,
							})}
							{this.renderAdsUnifiedNote(channel)}
						</>
					) : (
						<>
							<div className={css(styleSheet.adsFieldsGrid)}>
								{this.renderAdsChannelLiveDateField(channel)}
								<AccountDetailsInfoField label='Enabled Date'>
									{enabledDate ? getDefaultDateStringValue(moment.utc(enabledDate)) : '—'}
								</AccountDetailsInfoField>
								<AccountDetailsInfoField label='Last Meeting'>
									{lastMeeting
										? `${getDefaultDateStringValue(moment.utc(lastMeeting))}${lastStatus ? ` (${lastStatus})` : ''}`
										: '—'}
								</AccountDetailsInfoField>
								{this.renderAdsChannelNextMeetingField(channel)}
								{channel.key === 'facebook' && account ? (
									<ConnectedMetaAdAccountField impersonationContext={{ account }} />
								) : null}
							</div>
							<AccountDetailsNote type={channel.type} accountOperations={this.mAccountOperations} />
						</>
					)}
					{channel.key === 'facebook' && account ? (
						<MetaConnectionHealthSection impersonationContext={{ account }} />
					) : null}
					{channel.key === 'facebook' && account ? <AdsCampaignsTable impersonationContext={{ account }} /> : null}
				</AdsChannelCollapsibleSection>
			</div>
		);
	};

	/** Single-channel note rendering: prefer the channel-specific note, but if it
	 *  doesn't exist yet and the legacy `adsNoteId` does, render the legacy note
	 *  with `addTargetType` so new entries route to the channel. (Jes's behavior.) */
	private renderAdsUnifiedNote = (channel: IAdsChannelDescriptor) => {
		const additionalInfo = this.mAccountOperations.account?.additionalInfo;
		const channelNoteId = additionalInfo?.[channel.noteIdKey];
		const legacyNoteId = additionalInfo?.adsNoteId;
		if (!channelNoteId && legacyNoteId) {
			return (
				<AccountDetailsNote
					type={InternalMeetingNoteType.Ads}
					addTargetType={channel.type}
					accountOperations={this.mAccountOperations}
				/>
			);
		}
		return <AccountDetailsNote type={channel.type} accountOperations={this.mAccountOperations} />;
	};

	private renderAdsChannelNextMeetingField = (channel: IAdsChannelDescriptor) => {
		const nextMeeting = this.mAccountOperations.account?.additionalInfo?.[channel.nextMeetingKey];
		const isOpen = this.state.openNextAdsChannelMeetingSelector === channel.type;
		return (
			<AccountDetailsInfoField label='Next Meeting' info='Click the date to schedule this channel’s next meeting.'>
				<TinyPopover
					anchor={
						<span
							onClick={this.setNextAdsChannelMeetingSelectorOpen(channel.type, true)}
							className={css(styleSheet.nextMeetingValue)}
						>
							{nextMeeting ? getDefaultDateStringValue(moment.utc(nextMeeting)) : 'Not Scheduled'}
						</span>
					}
					isOpen={isOpen}
					dismissOnOutsideAction={true}
					onRequestClose={this.setNextAdsChannelMeetingSelectorOpen(channel.type, false)}
					align='center'
					placement={['top', 'bottom', 'left', 'right']}
				>
					<div className={css(styleSheet.dayPickerContainer)}>
						<DayPicker
							allowPastDates={false}
							className='date-range-picker-calendar-day-picker'
							onDayClick={(date: Date) => this.onNextAdsChannelMeetingDaySelected(channel.type, date)}
						/>
					</div>
				</TinyPopover>
			</AccountDetailsInfoField>
		);
	};

	private renderAdsCrossCuttingFields = (params: {
		isModifyingAccount: boolean;
		isEditMode: boolean;
		channelForLiveDate?: IAdsChannelDescriptor;
		/** Extra cells to append at the end of the field grid (e.g., a channel-specific field
		 *  surfaced in single-channel mode where there is no separate channel grid). */
		extraGridFields?: React.ReactNode;
	}) => {
		const { isModifyingAccount, isEditMode, channelForLiveDate, extraGridFields } = params;
		const account = this.mAccountOperations.account;
		if (!account) return null;
		const additionalInfo = account.additionalInfo;
		const { showNextAdsMeetingSelector } = this.state;
		const adsLiveDate = additionalInfo?.adsLiveDate;
		const enabledChannels = this.getEnabledAdsChannels();
		const pendingChannelLabels = enabledChannels.filter(c => !additionalInfo?.[c.liveDateKey]).map(c => c.label);
		return (
			<div className={css(styleSheet.adsFieldsGrid)}>
				<AccountDetailsInfoField label='Ads Specialist' isEditMode={isEditMode}>
					{isModifyingAccount
						? this.renderAdsSpecialistAutocomplete()
						: this.mAccountOperations.adsSpecialistDescription}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Kickoff Date' isEditMode={isEditMode}>
					{isEditMode ? (
						<DateInput value={this.getAdsKickoffDateValue()} onChange={this.onAdsKickoffDateChanged} />
					) : account.additionalInfo?.adsKickoffDate ? (
						this.formatAdsKickoffDateForDisplay(account.additionalInfo.adsKickoffDate)
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
				{channelForLiveDate ? (
					this.renderAdsChannelLiveDateField(channelForLiveDate)
				) : (
					<AccountDetailsInfoField
						label='Ads Live Date'
						info='Set automatically once every enabled channel has its own live date — the latest of those.'
					>
						<>
							<span>{adsLiveDate ? getDefaultDateStringValue(moment.utc(adsLiveDate)) : '-'}</span>
							{!adsLiveDate && pendingChannelLabels.length > 0 ? (
								<div className={css(styleSheet.adsFullyLivePending)}>
									Pending — not fully live across: {pendingChannelLabels.join(', ')}
								</div>
							) : null}
						</>
					</AccountDetailsInfoField>
				)}
				<AccountDetailsInfoField label='Last Ads Contact' isEditMode={isEditMode}>
					{isEditMode ? (
						<DateInput value={this.getLastAdsContactValue()} onChange={this.onLastAdsContactChanged} />
					) : account.additionalInfo?.lastAdsContact ? (
						getDefaultDateStringValue(moment.utc(account.additionalInfo.lastAdsContact))
					) : (
						'-'
					)}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField label='Last Ads Meeting' isEditMode={isEditMode}>
					{account.additionalInfo?.lastAdsMeeting
						? getDefaultDateStringValue(moment.utc(account.additionalInfo.lastAdsMeeting))
						: '-'}
				</AccountDetailsInfoField>
				<AccountDetailsInfoField
					label='Next Ads Meeting'
					isEditMode={isEditMode}
					info='You can click the date to edit the next meeting!'
				>
					<TinyPopover
						anchor={
							<span onClick={this.setNextAdsMeetingSelectorOpen(true)} className={css(styleSheet.nextMeetingValue)}>
								{account.additionalInfo?.nextAdsMeeting
									? getDefaultDateStringValue(moment.utc(account.additionalInfo.nextAdsMeeting))
									: 'Not Scheduled'}
							</span>
						}
						isOpen={showNextAdsMeetingSelector}
						dismissOnOutsideAction={true}
						onRequestClose={this.setNextAdsMeetingSelectorOpen(false)}
						align='center'
						placement={['top', 'bottom', 'left', 'right']}
					>
						<div className={css(styleSheet.dayPickerContainer)}>
							<DayPicker
								allowPastDates={false}
								className='date-range-picker-calendar-day-picker'
								onDayClick={this.onNextAdsMeetingDaySelected}
							/>
						</div>
					</TinyPopover>
				</AccountDetailsInfoField>
				{extraGridFields}
			</div>
		);
	};

	private renderAdsChannelLiveDateField = (channel: IAdsChannelDescriptor) => {
		const { editingAdsLiveDateChannel, adsLiveDateDraft, adsLiveDateSaving } = this.state;
		const liveDate = this.mAccountOperations.account?.additionalInfo?.[channel.liveDateKey];
		const isEditing = editingAdsLiveDateChannel === channel.type;
		return (
			<AccountDetailsInfoField label='Live Date'>
				{isEditing ? (
					<div className={css(styleSheet.adsLiveDateInputGroup)}>
						<DateInput value={adsLiveDateDraft || ''} onChange={value => this.setState({ adsLiveDateDraft: value })} />
						<button
							type='button'
							className={css(styleSheet.adsLiveDatePrimaryButton)}
							disabled={!!adsLiveDateSaving || !adsLiveDateDraft}
							onClick={this.onSaveAdsLiveDate}
						>
							Save
						</button>
						<button
							type='button'
							className={css(styleSheet.adsLiveDateSecondaryButton)}
							disabled={!!adsLiveDateSaving}
							onClick={this.onCancelEditAdsLiveDate}
						>
							Cancel
						</button>
					</div>
				) : (
					<div className={css(styleSheet.adsLiveDateRow)}>
						<span>{liveDate ? getDefaultDateStringValue(moment.utc(liveDate)) : '—'}</span>
						<button
							type='button'
							className={css(styleSheet.adsLiveDateButton)}
							onClick={() => this.onStartEditAdsLiveDate(channel.type, liveDate)}
						>
							{liveDate ? 'Edit' : 'Set'}
						</button>
						{liveDate ? (
							<button
								type='button'
								className={css(styleSheet.adsLiveDateButton)}
								disabled={!!adsLiveDateSaving}
								onClick={() => this.onClearAdsLiveDate(channel.type)}
							>
								Clear
							</button>
						) : null}
					</div>
				)}
			</AccountDetailsInfoField>
		);
	};

	private renderLegacyAdsNote() {
		const account = this.mAccountOperations.account;
		if (!account?.additionalInfo?.adsNoteId) return null;
		return (
			<AccountDetailsNote
				type={InternalMeetingNoteType.Ads}
				accountOperations={this.mAccountOperations}
				hideAddAction
			/>
		);
	}

	private onStartEditAdsLiveDate = (
		channel:
			| InternalMeetingNoteType.AdsFacebook
			| InternalMeetingNoteType.AdsGoogleLsa
			| InternalMeetingNoteType.AdsGooglePpc,
		currentLiveDate?: Date | string
	) => {
		const draft =
			currentLiveDate && moment(currentLiveDate).isValid() ? moment(currentLiveDate).format('YYYY-MM-DD') : '';
		this.setState({ editingAdsLiveDateChannel: channel, adsLiveDateDraft: draft });
	};

	private onCancelEditAdsLiveDate = () => {
		this.setState({ editingAdsLiveDateChannel: null, adsLiveDateDraft: '' });
	};

	private onSaveAdsLiveDate = () => {
		const { editingAdsLiveDateChannel, adsLiveDateDraft } = this.state;
		const { errorMessages, logApiError } = this.props;
		if (!editingAdsLiveDateChannel || !adsLiveDateDraft) return;
		const parsed = moment(adsLiveDateDraft, 'YYYY-MM-DD', true);
		if (!parsed.isValid()) {
			toast.error('Invalid date');
			return;
		}
		// updateAdsLiveDate returns null when AccountOperationsViewModel is busy.
		// Bail before flipping the saving flag — otherwise the button stays disabled forever.
		const promise = this.mAccountOperations.updateAdsLiveDate(editingAdsLiveDateChannel, parsed.toDate());
		if (!promise) return;
		this.setState({ adsLiveDateSaving: true });
		promise
			.then(() => {
				this.setState({
					editingAdsLiveDateChannel: null,
					adsLiveDateDraft: '',
					adsLiveDateSaving: false,
				});
			})
			.catch((error: Api.IOperationResultNoValue) => {
				this.setState({ adsLiveDateSaving: false });
				logApiError('UpdateAdsLiveDate-Error', error);
				errorMessages.pushApiError(error);
			});
	};

	private onClearAdsLiveDate = (
		channel:
			| InternalMeetingNoteType.AdsFacebook
			| InternalMeetingNoteType.AdsGoogleLsa
			| InternalMeetingNoteType.AdsGooglePpc
	) => {
		const { errorMessages, logApiError } = this.props;
		const promise = this.mAccountOperations.updateAdsLiveDate(channel, null);
		if (!promise) return;
		this.setState({ adsLiveDateSaving: true });
		promise
			.then(() => {
				this.setState({
					editingAdsLiveDateChannel: null,
					adsLiveDateDraft: '',
					adsLiveDateSaving: false,
				});
			})
			.catch((error: Api.IOperationResultNoValue) => {
				this.setState({ adsLiveDateSaving: false });
				logApiError('ClearAdsLiveDate-Error', error);
				errorMessages.pushApiError(error);
			});
	};

	private renderWebSpecialistAutocomplete() {
		return this.state.isEditMode ? (
			<AutoCompleteSearchField
				anchorClassName={css(styleSheet.searchBox)}
				dropdownContentClassName={css(styleSheet.autocompleteBox)}
				initialSearchQuery={this.mAccountOperations.webSpecialistDescription}
				inputId='employeeSearchBox3'
				inputProps={{ placeholder: 'Search' }}
				onItemSelected={this.onAutocompleteItemSelectedWebSpecialist}
				preventImpersonation={true}
				resultsLimit={5}
				type={Api.ResourceAutoCompleteViewModelType.User}
			/>
		) : (
			''
		);
	}

	private onAdminSendOnBehalfContactOwnerCriteriaChanged = (option: ISelectOption<Api.ContactOwnerCriteria>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			if (!draftAccount.preferences.resourceSelectorSettings) {
				draftAccount.preferences.resourceSelectorSettings = {} as any;
			}
			const relevantKeys = [
				Api.ResourceSelectorId.HappyBirthday,
				Api.ResourceSelectorId.PolicyRenew,
				Api.ResourceSelectorId.FinancialReview,
				Api.ResourceSelectorId.Turning65,
				Api.ResourceSelectorId.Turning72,
				Api.ResourceSelectorId.Turning73,
				Api.ResourceSelectorId.TurningXX,
				Api.ResourceSelectorId.Custom1,
				Api.ResourceSelectorId.Custom2,
				Api.ResourceSelectorId.Custom3,
				Api.ResourceSelectorId.Custom4,
				Api.ResourceSelectorId.Custom5,
				Api.ResourceSelectorId.Custom6,
				Api.ResourceSelectorId.Custom7,
				Api.ResourceSelectorId.Custom8,
				Api.ResourceSelectorId.Custom9,
				Api.ResourceSelectorId.Custom10,
				Api.ResourceSelectorId.NewDonations,
			];

			// iterate over each relevant key and set the contact owner criteria
			relevantKeys.forEach(key => {
				if (!draftAccount.preferences.resourceSelectorSettings[key]) {
					draftAccount.preferences.resourceSelectorSettings[key] = {} as any;
				}
				draftAccount.preferences.resourceSelectorSettings[key].contactOwnerCriteria = option.dataContext;
			});
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private loadAccount = () => {
		const { errorMessages } = this.props;
		this.mAccountOperations
			.load(this.state.account)
			.then(value => {
				this.applyAccount(value);
			})
			.catch(() => {
				errorMessages.push({
					messages: ['Could not load the account information'],
				});
			});
	};

	private applyAccount = (account: Api.IAccount, isEditMode = false) => {
		if (!account.preferences) {
			account.preferences = {
				disableCalendarContactSync: false,
				disableSentMailOpenTracking: false,
				ignoreInternalMeetings: true,
				ownerTagAlertRecipients: undefined,
			};
		}
		if (!account.preferences.defaults) {
			account.preferences.defaults = {
				userRole: 'user',
				visibility: 'all',
			};
		}
		if (!account.features) {
			account.features = {};
		}
		if (!account.features.boards) {
			account.features.boards = {
				opportunitiesEnabled: false,
			};
		}

		const savedAccount = JSON.parse(JSON.stringify(account));

		this.updateEditableAccount(
			savedAccount,
			{
				isEditMode,
				savedAccount,
				settingsContext: { ...this.state.settingsContext },
			},
			() => {
				document.title = 'Admin | ' + this.state?.account?.companyName;
			}
		);

		this.mImpersonationProviderProps.impersonationContext.update({ account });
		this.props.onAccountUpdated?.(account);
		if (!isEditMode) {
			this.props.onEditModeChange?.(false);
		}
	};

	private updateEditableAccount = (account: Api.IAccount, additionalStateChanges?: IState, onFinish?: () => void) => {
		this.setState(
			{
				account,
				context: { ...this.state.context, account },
				...(additionalStateChanges || {}),
			},
			onFinish
		);
	};

	private hasAdsComponents = (account: Api.IAccount): boolean => {
		// Check if account has any Facebook or Google components (case insensitive)
		const components = account?.planDetails?.additionalComponents ?? [];
		return components.some(component => {
			const lowerComponent = component.toLowerCase();
			return lowerComponent.includes('facebook') || lowerComponent.includes('google');
		});
	};

	/** Enter edit mode without notifying parent (used by externalEditMode sync) */
	private enterEditMode = () => {
		const account = { ...this.mAccountOperations.account };
		this.setState({
			account,
			isEditMode: true,
			settingsContext: { ...this.state.settingsContext },
		});
	};

	private accountEditClick = () => {
		this.enterEditMode();
		this.props.onEditModeChange?.(true);
	};

	/** Exit edit mode without notifying parent (used by externalEditMode sync) */
	private exitEditMode = () => {
		const { savedAccount, settingsContext } = this.state;
		const account = JSON.parse(JSON.stringify(savedAccount));
		this.updateEditableAccount(account, {
			isEditMode: false,
			settingsContext: { ...settingsContext },
		});
	};

	private onCancelEdit = () => {
		this.exitEditMode();
		this.props.onEditModeChange?.(false);
	};

	private saveAccount = () => {
		const { errorMessages } = this.props;
		const { account } = this.state;

		const nextAccount = produce(account, draftAccount => {
			// Convert YYYY-MM-DD date strings from DateInput to ISO with noon time to avoid timezone issues
			const currentKickoffDate = draftAccount.additionalInfo?.adsKickoffDate;
			if (currentKickoffDate && typeof currentKickoffDate === 'string' && currentKickoffDate.trim()) {
				const parsed = moment(currentKickoffDate, 'YYYY-MM-DD', true);
				draftAccount.additionalInfo.adsKickoffDate = parsed.isValid()
					? parsed.hour(12).minute(0).second(0).millisecond(0).toISOString()
					: draftAccount.additionalInfo.adsKickoffDate;
			}

			const currentLastAdsContact = draftAccount.additionalInfo?.lastAdsContact;
			if (currentLastAdsContact && typeof currentLastAdsContact === 'string' && currentLastAdsContact.trim()) {
				const parsed = moment(currentLastAdsContact, 'YYYY-MM-DD', true);
				draftAccount.additionalInfo.lastAdsContact = parsed.isValid()
					? parsed.hour(12).minute(0).second(0).millisecond(0).toISOString()
					: draftAccount.additionalInfo.lastAdsContact;
			}
		});

		const managementAccountId = isNorthwesternMutualBrokerDealer(nextAccount)
			? nextAccount.managementAccountId || ''
			: '';
		const shouldSaveManagementAccountId = (this.state.savedAccount?.managementAccountId || '') !== managementAccountId;

		const promise = this.mAccountOperations.saveAccount(nextAccount);
		if (promise) {
			promise
				.then(async value => {
					try {
						let savedAccount = value;
						if (account?.planDetails?.billingType === Api.BillingType.Comp) {
							savedAccount = await this.mAccountOperations.saveMainAccountId(nextAccount);
						}

						if (shouldSaveManagementAccountId) {
							savedAccount = await this.mAccountOperations.saveManagementAccountId({
								...savedAccount,
								managementAccountId,
							});
						}

						this.applyAccount(savedAccount);
					} catch (error) {
						errorMessages.pushApiError(error);
					}
				})
				.catch((error: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(error);
				});
		}
	};

	private assessmentModalClose = () => {
		this.setState({ showAssessment: false });
	};

	private generateAssessmentClick = () => {
		this.setState({ showAssessment: true });
	};

	private onImportTemplates = () => {
		this.setState({ showImportTemplatesModal: true });
	};

	private onImportTemplatesModalClose = () => {
		this.setState({ showImportTemplatesModal: false });
	};

	private onFindRecordingsClick = () => {
		this.setState({ showFindRecordingsModal: true });
	};

	private onFindRecordingsModalClose = () => {
		this.setState({ showFindRecordingsModal: false });
	};

	private onAutocompleteItemSelectedWebSpecialist = (user: Api.IUser) => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.webSpecialistId = user.id;
		account.additionalInfo.webSpecialistName = user.firstName + ' ' + user.lastName;
		this.updateEditableAccount(account);
	};

	private renderAdsSpecialistAutocomplete() {
		return this.state.isEditMode ? (
			<AutoCompleteSearchField
				anchorClassName={css(styleSheet.searchBox)}
				dropdownContentClassName={css(styleSheet.autocompleteBox)}
				initialSearchQuery={this.mAccountOperations.adsSpecialistDescription}
				inputId='employeeSearchBoxAds'
				inputProps={{ placeholder: 'Search' }}
				onItemSelected={this.onAutocompleteItemSelectedAdsSpecialist}
				preventImpersonation={true}
				resultsLimit={5}
				type={Api.ResourceAutoCompleteViewModelType.User}
			/>
		) : (
			''
		);
	}

	private onAutocompleteItemSelectedAdsSpecialist = (user: Api.IUser) => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.adsSpecialistId = user.id;
		account.additionalInfo.adsSpecialistName = user.firstName + ' ' + user.lastName;
		this.updateEditableAccount(account);
	};

	private onAdsStageChanged = (option: IAdsStageOption | null): void => {
		if (option) {
			const account: Api.IAccount = { ...this.state.account };
			account.additionalInfo.adsStage = option.value;
			this.updateEditableAccount(account);
		}
	};

	private setNextAdsMeetingSelectorOpen = (open: boolean) => () => {
		this.setState({ showNextAdsMeetingSelector: open });
	};

	private onNextAdsMeetingDaySelected = (nextMeeting: Date) => {
		const { errorMessages, logApiError } = this.props;
		this.setState({ showNextAdsMeetingSelector: false });

		this.mAccountOperations
			.updateNextAdsMeeting(nextMeeting)
			?.then(() => {
				toast.success('Next ads meeting updated successfully');
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateNextAdsMeeting-Error', err);
				toast.error('Failed to update next ads meeting.');
			});
	};

	private setNextAdsChannelMeetingSelectorOpen =
		(
			channel:
				| InternalMeetingNoteType.AdsFacebook
				| InternalMeetingNoteType.AdsGoogleLsa
				| InternalMeetingNoteType.AdsGooglePpc,
			open: boolean
		) =>
		() => {
			this.setState({ openNextAdsChannelMeetingSelector: open ? channel : null });
		};

	private onNextAdsChannelMeetingDaySelected = (
		channel:
			| InternalMeetingNoteType.AdsFacebook
			| InternalMeetingNoteType.AdsGoogleLsa
			| InternalMeetingNoteType.AdsGooglePpc,
		nextMeeting: Date
	) => {
		const { errorMessages, logApiError } = this.props;
		this.setState({ openNextAdsChannelMeetingSelector: null });

		this.mAccountOperations
			.updateNextAdsMeeting(nextMeeting, channel)
			?.then(() => {
				toast.success('Next meeting updated successfully');
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateNextAdsChannelMeeting-Error', err);
				toast.error('Failed to update next meeting.');
			});
	};

	private getLastAdsContactValue(): string {
		const dateValue = this.state.account?.additionalInfo?.lastAdsContact;
		if (!dateValue) return '';
		const date = moment(dateValue);
		return date.isValid() ? date.format('YYYY-MM-DD') : '';
	}

	private onLastAdsContactChanged = (value: string): void => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.lastAdsContact = value || undefined;
		this.updateEditableAccount(account);
	};

	/**
	 * Formats ads kickoff date for display, handling both ISO date strings and Date objects
	 * Always returns MM/DD/YYYY format for display purposes
	 */
	private formatAdsKickoffDateForDisplay = (date: string | Date): string => {
		if (!date) {
			return '';
		}

		// If it's already in MM/DD/YYYY format, return as-is
		if (typeof date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
			return date;
		}

		// For ISO strings or Date objects, format to MM/DD/YYYY using UTC so date-only
		// fields stored at UTC midnight don't shift back a day in local timezones.
		try {
			return moment.utc(date).format('MM/DD/YYYY');
		} catch {
			// If parsing fails, return the string as-is
			return typeof date === 'string' ? date : '';
		}
	};

	private getAdsKickoffDateValue(): string {
		const dateValue = this.state.account?.additionalInfo?.adsKickoffDate;
		if (!dateValue) return '';
		const date = moment(dateValue);
		return date.isValid() ? date.format('YYYY-MM-DD') : '';
	}

	private onAdsKickoffDateChanged = (value: string): void => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo.adsKickoffDate = value || undefined;
		this.updateEditableAccount(account);
	};

	private onInternalMeetingsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.ignoreInternalMeetings = !option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onHtmlNewsletterChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		if (option) {
			this.setState({ showUnsubscribeSignatureHtmlModal: true });
		} else {
			this.mAccountOperations
				.updateHtmlNewsletterFeatures({
					...account.features.htmlNewsletter,
					enabled: false,
				})
				?.then(result => {
					this.updateHtmlNewsletterFeature(result.value);
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
					logApiError('UpdateHtmlNewsletterFeaturesError', err);
				});
		}
	};

	private onEditHtmlNewsletterFooter = () => {
		this.setState({ showUnsubscribeSignatureHtmlModal: true });
	};
	private onDeleteRenewalKeyFactsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			{
				...account,
				preferences: {
					...account.preferences,
					deleteRenewalKeyFacts: option,
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onResetAccountClicked = () => {
		this.setState({ showAccountResetModal: true });
	};

	private onMeetingSchedulerChanged = (feature: Api.IMeetingSchedulerFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateMeetingSchedulerFeatures(feature)
			?.then(result => {
				this.updateMeetingSchedulerFeature(result.value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateMeetingSchedulerFeaturesError', err);
			});
	};

	private onMeetingSchedulerEnabled = (enabled: boolean) => {
		this.onMeetingSchedulerChanged({
			...this.state.account.features.meetingScheduler,
			enabled,
		});
	};

	private onZoomChanged = (feature: Api.IZoomFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateZoomFeatures(feature)
			?.then(result => {
				this.updateZoomFeature(result.value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateZoomFeaturesError', err);
			});
	};

	private onZoomEnabled = (enabled: boolean) => {
		this.onZoomChanged({
			...this.state.account.features.zoom,
			enabled,
		});
	};

	private onMeetingCompanyNameRequiredChanged = (enabled: boolean) => {
		this.onMeetingSchedulerChanged({
			...this.state.account.features.meetingScheduler,
			allowCompanyNameRequired: enabled,
		});
	};

	private onEmailEngagementTaggingChanged = (enabled: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		this.mAccountOperations
			.updateEmailEngagementTaggingFeatures({
				...(savedAccount?.features?.emailEngagementTagging || {}),
				enabled,
			})
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateEditableAccount(
						produce(this.state.account, draft => {
							draft.features = draft.features || {};
							draft.features.emailEngagementTagging = result.value;
						})
					);
				} else {
					toast.error('Failed to update Email Engagement Tagging settings.');
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateEmailEngagementTaggingFeaturesError', err);
			});
	};

	private onAIEmailCampaignChanged = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setAIEmailCampaignFeature({
			...(savedAccount?.features?.aiEmailCampaign || {}),
			enabled,
		});
	};

	private onAIEmailCampaignQuotaChanged = (quota: Partial<Api.IAIEmailCampaignFeature>) => {
		const { savedAccount } = this.state;
		this.setAIEmailCampaignFeature({
			...(savedAccount?.features?.aiEmailCampaign || {}),
			...quota,
		});
	};

	private setAIEmailCampaignFeature = (feature: Api.IAIEmailCampaignFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateAIEmailCampaignFeatures(feature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateEditableAccount(
						produce(this.state.account, draft => {
							draft.features = draft.features || {};
							draft.features.aiEmailCampaign = result.value;
						})
					);
				} else {
					toast.error('Failed to update AI Email Campaign settings.');
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAIEmailCampaignFeaturesError', err);
			});
	};

	private onKeepInTouchAgentChanged = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setKeepInTouchAgentFeature({
			...(savedAccount?.features?.keepInTouch || {}),
			enabled,
		});
	};

	private onKeepInTouchAgentDailyScanQuotaChanged = (value: number) => {
		const { savedAccount } = this.state;
		this.setKeepInTouchAgentFeature({
			...(savedAccount?.features?.keepInTouch || {}),
			maxContactScansPerDay: value,
		});
	};

	private onKeepInTouchAgentMinDaysBetweenScansChanged = (value: number) => {
		const { savedAccount } = this.state;
		this.setKeepInTouchAgentFeature({
			...(savedAccount?.features?.keepInTouch || {}),
			minDaysBetweenScans: value,
		});
	};

	private setKeepInTouchAgentFeature = (feature: Api.IKeepInTouchAgentFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateKeepInTouchAgentFeatures(feature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateEditableAccount(
						produce(this.state.account, draft => {
							draft.features = draft.features || {};
							draft.features.keepInTouch = result.value;
						})
					);
				} else {
					toast.error('Failed to update Keep In Touch Agent settings.');
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateKeepInTouchAgentFeaturesError', err);
			});
	};

	private onTextingChanged = (option: boolean) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			enabled: option,
		});
	};

	private onTextingRequireArchivingChanged = (option: boolean) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			requiresArchiving: option,
		});
	};

	private onTextingZoomPhoneEnabledChanged = (option: boolean) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			zoomPhoneEnabled: option,
		});
	};

	private onTextingUseParentCampaignRegistrationChanged = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			useParentTextingCampaignRegistration: enabled,
		});
	};

	private onHandwrittenCardsEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const handwrittenFeature = {
			...(savedAccount?.features?.handwrittenCards || {}),
			enabled: option,
		};
		this.mAccountOperations
			.updateHandwrittenFeatures(handwrittenFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHandwrittenFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHandwrittenCardFeaturesError', err);
			});
	};

	private onHandwrittenCardsProviderChanged = (option: ISelectOption<Api.HandwrittenCardProvider>) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const handwrittenFeature = {
			...(savedAccount?.features?.handwrittenCards || {}),
			provider: option.dataContext,
		};
		this.mAccountOperations
			.updateHandwrittenFeatures(handwrittenFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHandwrittenFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHandwrittenCardFeaturesError', err);
			});
	};

	private onHouseholdsEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const householdsFeature = {
			...(savedAccount?.features?.households || {}),
			enabled: option,
		};
		this.mAccountOperations
			.updateHouseholdsFeature(householdsFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHouseholdsFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHouseholdsFeaturesError', err);
			});
	};

	private onHouseholdsSendToHouseholdByDefaultChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount } = this.state;
		const householdsFeature = {
			...(savedAccount?.features?.households || {}),
			sendToHouseholdByDefault: option,
		};
		this.mAccountOperations
			.updateHouseholdsFeature(householdsFeature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateHouseholdsFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHouseholdsFeaturesError', err);
			});
	};

	private onTextingDailySendLimitChanged = (count: number) => {
		const { savedAccount } = this.state;
		if (savedAccount?.features?.texting?.sendLimits == null) {
			return;
		}
		const sendLimits = savedAccount.features.texting.sendLimits.map(i =>
			i.intervalInMinutes === 1440 ? { count, intervalInMinutes: 1440 } : i
		);
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			sendLimits,
		});
	};

	private onTextingHourlySendLimitChanged = (count: number) => {
		const { savedAccount } = this.state;
		if (savedAccount?.features?.texting?.sendLimits == null) {
			return;
		}
		const hasHourlyLimit = savedAccount.features.texting.sendLimits.some(i => i.intervalInMinutes === 60);
		const sendLimits = hasHourlyLimit
			? savedAccount.features.texting.sendLimits.map(i =>
					i.intervalInMinutes === 60 ? { count, intervalInMinutes: 60 } : i
				)
			: [...savedAccount.features.texting.sendLimits, { count, intervalInMinutes: 60 }];
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			sendLimits,
		});
	};

	private onTextingSecondsBetweenSendsChanged = (seconds: number) => {
		const { savedAccount } = this.state;
		this.setTextingFeature({
			...(savedAccount?.features?.texting || {}),
			secondsBetweenSends: seconds,
		});
	};

	private onTurningXXInputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX =
				draftAccount.preferences.resourceSelectorSettings.TurningXX || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX2InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX2 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX2 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX2.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX2._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX3InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX3 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX3 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX3.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX3._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX4InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX4 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX4 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX4.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX4._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTurningXX5InputChanged = (value: number) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX5 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX5 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX5.age = value;
			draftAccount.preferences.resourceSelectorSettings.TurningXX5._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private setTextingFeature = (feature: Api.ITextingFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateTextingFeatures(feature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateTextingFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateTextingFeaturesError', err);
			});
	};

	private setDefaultEmailStyleFeature = (feature: Api.IDefaultEmailStyleFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateDefaultEmailStyleFeatures(feature)
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateDefaultEmailStyleFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateDefaultEmailStyleFeaturesError', err);
			});
	};

	private onEnableSurveysChanged = (option: boolean) => {
		const { savedAccount } = this.state;
		this.setSurveysFeature({
			...(savedAccount?.features?.surveys || {}),
			enabled: option,
		});
	};

	private onEnableFilesChanged = (option: boolean) => {
		const { savedAccount } = this.state;

		this.onSetCustomerFileFeature({
			...savedAccount?.features?.files,
			enabled: option,
		});
	};

	private onCanSendFileLinksChanged = (option: boolean) => {
		const { savedAccount } = this.state;

		this.onSetCustomerFileFeature({
			...savedAccount?.features?.files,
			canSendFileLinks: option,
		});
	};

	private onSetSurveyDefaultReviewLink = (defaultReviewLink?: string) => {
		const { savedAccount } = this.state;
		this.setSurveysFeature({
			...(savedAccount?.features?.surveys || {}),
			defaultReviewLink,
		});
	};

	private onSetSurveyNewReviewsEmailNotification = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setSurveysFeature({
			...(savedAccount?.features?.surveys || {}),
			newReviewsEmailNotification: enabled,
		});
	};

	private onSetCustomerFileFeature = (feature: Api.IFilesFeature) => {
		const { errorMessages, logApiError } = this.props;

		this.mAccountOperations
			.updateCustomerFilesFeatures(feature)
			?.then(result => {
				const { savedAccount, account } = this.state;
				console.log(result);
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.files = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.files = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateFilesFeatureError', err);
			});
	};

	private setSurveysFeature = (feature: Api.ISurveysFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateSurveyFeatures(feature)
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.surveys = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.surveys = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSurveysFeaturesError', err);
			})
			?.finally(() => {
				invalidateAccountAdminQuery();
			});
	};

	private onEnableSocialMediaChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;
		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: option,
				alwaysOverlayLogo: option ? savedAccount?.features?.socialMedia?.alwaysOverlayLogo : false,
				hideWhiteBackground: option ? savedAccount?.features?.socialMedia?.hideWhiteBackground : false,
				readOnly: option ? savedAccount?.features?.socialMedia?.readOnly : false,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableSocialMediaLogoOverlayChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;
		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: true,
				alwaysOverlayLogo: option,
				hideWhiteBackground: option ? account?.features?.socialMedia?.hideWhiteBackground : false,
				readOnly: option ? savedAccount?.features?.socialMedia?.readOnly : false,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableSocialMediaLogoBackgroundChanged: (option: boolean) => void = option => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;
		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: true,
				alwaysOverlayLogo: account?.features?.socialMedia?.alwaysOverlayLogo,
				hideWhiteBackground: option,
				readOnly: savedAccount?.features?.socialMedia?.readOnly,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableSocialMediaReadOnly: (option: boolean) => void = option => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;

		if (!option) {
			errorMessages.push({
				messages: ['Turning off social media read only configuration requires an account maintenance ticket.'],
			});
			return;
		}

		this.mAccountOperations
			.updateSocialMediaFeatures({
				enabled: true,
				alwaysOverlayLogo: account?.features?.socialMedia?.alwaysOverlayLogo,
				hideWhiteBackground: account?.features?.socialMedia?.hideWhiteBackground,
				readOnly: option,
			})
			?.then(result => {
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.socialMedia = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.socialMedia = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateSocialMediaFeatures-Error', err);
			});
	};

	private onEnableTranslationChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateTranslationFeatures({
				enabled: option,
			})
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.translation = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.translation = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateTranslationFeatures-Error', err);
			});
	};

	private onEnableContentGenerationChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		const existing = account?.features?.contentGeneration;
		this.mAccountOperations
			.updateContentGenerationFeatures({
				// Whole-object replace: round-trip the existing feature (betaFeatures,
				// allowedExternalDataSources) so toggling enabled doesn't wipe them.
				...existing,
				enabled: option,
			})
			?.then(result => {
				const { savedAccount, account: currentAccount } = this.state;
				this.updateEditableAccount(
					produce(currentAccount, draft => {
						draft.features = draft.features || {};
						draft.features.contentGeneration = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.contentGeneration = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateContentGenerationFeatures-Error', err);
			});
	};

	private onAllowIntegrationsChanged = (allow: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		const existing = account?.features?.contentGeneration;
		const currentAllowlist = existing?.allowedExternalDataSources ?? [];
		// Add/remove only the Integration entry so other allowlist values are preserved.
		const allowedExternalDataSources = allow
			? currentAllowlist.includes(Api.AIAgentExternalDataSource.Integration)
				? currentAllowlist
				: [...currentAllowlist, Api.AIAgentExternalDataSource.Integration]
			: currentAllowlist.filter(source => source !== Api.AIAgentExternalDataSource.Integration);
		this.mAccountOperations
			.updateContentGenerationFeatures({
				// The PUT is a whole-object replace, so round-trip the existing feature (preserves
				// betaFeatures and anything the backend adds later) and override only what we change.
				...existing,
				// Coerce to a concrete boolean so we never PUT `enabled: undefined` if contentGeneration is absent.
				enabled: !!existing?.enabled,
				allowedExternalDataSources,
			})
			?.then(result => {
				const { savedAccount, account: currentAccount } = this.state;
				this.updateEditableAccount(
					produce(currentAccount, draft => {
						draft.features = draft.features || {};
						draft.features.contentGeneration = result.value as Api.IContentGenerationFeature;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.contentGeneration = result.value as Api.IContentGenerationFeature;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateContentGenerationFeatures-Error', err);
			});
	};

	private onEnableMeetingPrepChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		// Preserve any existing cap; default to 20 when first enabling.
		const maxBriefsPerUserPerDay = account?.features?.meetingPrep?.maxBriefsPerUserPerDay ?? 20;
		this.mAccountOperations
			.updateMeetingPrepFeatures({
				enabled: option,
				maxBriefsPerUserPerDay,
			})
			?.then(result => {
				const { savedAccount, account: currentAccount } = this.state;
				this.updateEditableAccount(
					produce(currentAccount, draft => {
						draft.features = draft.features || {};
						draft.features.meetingPrep = result.value as Api.IMeetingPrepFeature;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.meetingPrep = result.value as Api.IMeetingPrepFeature;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateMeetingPrepFeatures-Error', err);
			});
	};

	private onEnableEmailExecChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		const emailExec = account?.features?.emailExec;
		this.mAccountOperations
			.updateEmailExecFeatures({
				enabled: option,
				maxScheduledDigestsPerDay: emailExec?.maxScheduledDigestsPerDay,
				maxAdhocRunsPerDay: emailExec?.maxAdhocRunsPerDay,
			})
			?.then(result => {
				const { savedAccount, account: currentAccount } = this.state;
				this.updateEditableAccount(
					produce(currentAccount, draft => {
						draft.features = draft.features || {};
						draft.features.emailExec = result.value as Api.EmailExecFeature;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.emailExec = result.value as Api.EmailExecFeature;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateEmailExecFeatures-Error', err);
			});
	};

	private onEnableFileClassificationChanged = (option: boolean) => {
		const { account } = this.state;
		this.setFileClassificationFeature({ ...account?.features?.fileClassification, enabled: option });
	};

	private onFileClassificationDailyLimitChanged = (value: number | null) => {
		const { account } = this.state;
		this.setFileClassificationFeature({ ...account?.features?.fileClassification, dailyLimit: value });
	};

	private setFileClassificationFeature = (feature: Api.IFileClassificationFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateFileClassificationFeatures(feature)
			?.then(result => {
				const { savedAccount, account: currentAccount } = this.state;
				this.updateEditableAccount(
					produce(currentAccount, draft => {
						draft.features = draft.features || {};
						draft.features.fileClassification = result.value as Api.IFileClassificationFeature;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.fileClassification = result.value as Api.IFileClassificationFeature;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateFileClassificationFeatures-Error', err);
			});
	};

	private onAllowEmailContentChanged = (allow: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		const existing = account?.features?.contentGeneration;
		const currentAllowlist = existing?.allowedExternalDataSources ?? [];
		// Add/remove only the EmailProvider entry so other allowlist values are preserved.
		const allowedExternalDataSources = allow
			? currentAllowlist.includes(Api.AIAgentExternalDataSource.EmailProvider)
				? currentAllowlist
				: [...currentAllowlist, Api.AIAgentExternalDataSource.EmailProvider]
			: currentAllowlist.filter(source => source !== Api.AIAgentExternalDataSource.EmailProvider);
		this.mAccountOperations
			.updateContentGenerationFeatures({
				// The PUT is a whole-object replace, so round-trip the existing feature (preserves
				// betaFeatures and anything the backend adds later) and override only what we change.
				...existing,
				// Coerce to a concrete boolean so we never PUT `enabled: undefined` if contentGeneration is absent.
				enabled: !!existing?.enabled,
				allowedExternalDataSources,
			})
			?.then(result => {
				const { savedAccount, account: currentAccount } = this.state;
				this.updateEditableAccount(
					produce(currentAccount, draft => {
						draft.features = draft.features || {};
						draft.features.contentGeneration = result.value as Api.IContentGenerationFeature;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.contentGeneration = result.value as Api.IContentGenerationFeature;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateContentGenerationFeatures-Error', err);
			});
	};

	private onEnableCustomContentCalendarChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateCustomContentCalendarFeatures({ enabled: option })
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.customContentCalendar = result.value as Api.ICustomContentCalendarFeature;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.customContentCalendar = result.value as Api.ICustomContentCalendarFeature;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomContentCalendarFeatures-Error', err);
			});
	};

	private onEnablePublicAPIChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updatePublicAPIFeatures({
				enabled: option,
			})
			?.then(result => {
				const { savedAccount, account } = this.state;
				this.updateEditableAccount(
					produce(account, draft => {
						draft.features = draft.features || {};
						draft.features.publicApi = result.value;
					}),
					{
						savedAccount: produce(savedAccount, draft => {
							draft.features = draft.features || {};
							draft.features.publicApi = result.value;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateContentGenerationFeatures-Error', err);
			});
	};

	private onEnableStripePaymentsChanged = async (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { savedAccount, account } = this.state;

		try {
			const result = await this.mAccountOperations.updateStripePaymentsFeatures({
				enabled: option,
			});

			this.updateEditableAccount(
				produce(account, draft => {
					draft.features = draft.features || {};
					draft.features.stripePayment.enabled = result.value.enabled;
				}),
				{
					savedAccount: produce(savedAccount, draft => {
						draft.features = draft.features || {};
						draft.features.stripePayment.enabled = result.value.enabled;
					}),
				}
			);
		} catch (err) {
			errorMessages.pushApiError(err);
			logApiError('UpdateStripePaymentsFeatures-Error', err);
		}
	};

	private onTimelineChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateTimelineFeatures({
				enabled: option,
			})
			?.then(result => {
				this.updateTimelineFeature(result.value);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateTimelineFeaturesError', err);
			});
	};

	private onUnsubscribeSignatureModalHtmlRequestClose = (htmlNewsletterFeatures?: Api.IHtmlNewsletterFeatures) => {
		if (htmlNewsletterFeatures) {
			this.updateHtmlNewsletterFeature(htmlNewsletterFeatures);
		}
		this.setState({ showUnsubscribeSignatureHtmlModal: false });
	};

	private onAccountResetModalRequestClose = (reset?: boolean, includeIntegration?: boolean, canceled?: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;

		if (reset && !canceled && !!account?.id) {
			if (includeIntegration) {
				this.mAccountOperations
					.resetAccountAndIntegration(account)
					?.then(() => {
						toast.success(`Account and integration data successfully reset`);
					})
					?.catch((error: Api.IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
						logApiError('ResetAccountAndIntegrationError', error);
						return;
					});
			} else {
				this.mAccountOperations
					.resetAccount(account)
					?.then(() => {
						toast.success(`Account successfully reset`);
					})
					?.catch((error: Api.IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
						logApiError('ResetAccountError', error);
					});
			}
		}

		this.setState({ showAccountResetModal: false });
	};

	private updateMeetingSchedulerFeature = (meetingSchedulerFeatures: Api.IMeetingSchedulerFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.meetingScheduler = meetingSchedulerFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.meetingScheduler = meetingSchedulerFeatures;
				}),
			}
		);
	};

	private updateZoomFeature = (zoomFeature: Api.IZoomFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.zoom = zoomFeature;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.zoom = zoomFeature;
				}),
			}
		);
	};

	private updateTextingFeature = (textingFeatures: Api.IFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.texting = textingFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.texting = textingFeatures;
				}),
			}
		);
	};

	private updateDefaultEmailStyleFeature = (defaultEmailStyleFeatures: Api.IDefaultEmailStyleFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.defaultEmailStyle = defaultEmailStyleFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.defaultEmailStyle = defaultEmailStyleFeatures;
				}),
			}
		);
	};

	private updateHandwrittenFeature = (handwrittenCardFeatures: Api.IFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.handwrittenCards = handwrittenCardFeatures;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.handwrittenCards = handwrittenCardFeatures;
				}),
			}
		);
	};

	private updateHouseholdsFeature = (householdsFeature: Api.IHouseholdsFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.households = householdsFeature;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.households = householdsFeature;
				}),
			}
		);
	};

	private updateTimelineFeature = (timelineFeature: Api.IFeature) => {
		const { account, savedAccount } = this.state;
		this.updateEditableAccount(
			produce(account, draft => {
				draft.features.timeline = timelineFeature;
			}),
			{
				savedAccount: produce(savedAccount, draft => {
					draft.features.timeline = timelineFeature;
				}),
			}
		);
	};

	private updateHtmlNewsletterFeature = (htmlNewsletterFeatures: Api.IHtmlNewsletterFeatures) => {
		const { account } = this.state;

		this.applyAccount(
			{
				...account,
				features: {
					...account?.features,
					htmlNewsletter: htmlNewsletterFeatures,
				},
			},
			false
		);
	};
	private updateClickTracking = (shouldTrack: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.clickTrackingEnabled = shouldTrack;
			}),
			undefined
		);
	};

	private updateCsmViewMyCampaignsEnabled = (canView: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.csmViewMyCampaignsEnabled = canView;
			}),
			undefined
		);
	};

	private updateEmailScanFeatures = (emailScanFeatures: Api.IEmailScanFeatures) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features.emailScan = emailScanFeatures;
			}),
			undefined
		);
	};

	private onSetMaximumGroupAutomationContacts = (option: ISelectOption<number>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.maximumGroupAutomationContacts = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetMaximumGroupSendContacts = (option: ISelectOption<number>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.maximumGroupSendContacts = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetMaximumHTMLNewsletterContacts = (option: ISelectOption) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		this.mAccountOperations
			.updateHtmlNewsletterFeatures({
				...account.features.htmlNewsletter,
				maximumContactsPerSend: option.dataContext,
			})
			?.then(result => {
				this.updateHtmlNewsletterFeature(result.value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHtmlNewsletterFeaturesError', err);
			});
	};

	private onCreateContactsFromCalendarChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.disableCalendarContactSync = !option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onClassificationEmailsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.classificationEmailsEnabled = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSentMailOpenTrackingChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.disableSentMailOpenTracking = !option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSentMailClickTrackingChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations
			.updateClickTracking(enabled)
			?.then(() => {
				this.updateClickTracking(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateClickTrackingError', err);
			});
	};

	private onCsmViewMyCampaignsEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateCsmViewMyCampaignsEnabled(enabled)
			?.then(() => {
				this.updateCsmViewMyCampaignsEnabled(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCsmViewMyCampaignsEnabledxError', err);
			});
	};

	private onDataBoardToggleChanged = (featureName: BooleanKeys<Api.IBoardFeatures>, newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				draftAccount.features.boards[featureName] = newValue;
				switch (featureName) {
					case 'policiesEnabled':
						if (draftAccount.features.boards.policyBoard === undefined) {
							draftAccount.features.boards.policyBoard = {
								type: 'Policy',
								enabled: newValue,
								configuration: {
									tagSettings: {
										carrierTagsEnabled: false,
										lobTagEnabled: true, // This is defaulted to true as it on by default on the backend.
									},
								},
							};
						} else {
							draftAccount.features.boards.policyBoard.enabled = newValue;
						}
						break;
					case 'donationsEnabled':
						if (draftAccount.features.boards.donationBoard === undefined) {
							draftAccount.features.boards.donationBoard = {
								type: 'Policy',
								enabled: newValue,
								configuration: {},
							};
						} else {
							draftAccount.features.boards.donationBoard.enabled = newValue;
						}
						break;
					default:
						break;
				}
			}),
			undefined,
			this.saveAccount
		);
	};
	private onDataBoardDonationsEnabledChanged = (newValue: boolean) => {
		this.onDataBoardToggleChanged('donationsEnabled', newValue);
	};

	private onDonationTagSettingsChanged = (settings: Omit<Api.IDonationDataboardTagSettings, '_type'>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				if (!draftAccount.features.boards.donationBoard) {
					return;
				}
				draftAccount.features.boards.donationBoard.configuration =
					draftAccount.features.boards.donationBoard.configuration || {};
				const tagSettings = (draftAccount.features.boards.donationBoard.configuration.tagSettings ||
					{}) as Api.IDonationDataboardTagSettings;
				tagSettings._type = 'DonationDataboardTagSettings';
				tagSettings.enabled = settings.enabled;
				tagSettings.lastDonationTagsEnabled = settings.lastDonationTagsEnabled;
				tagSettings.lifetimeDonationTagsEnabled = settings.lifetimeDonationTagsEnabled;
				tagSettings.yearlyDonationTagsEnabled = settings.yearlyDonationTagsEnabled;
				draftAccount.features.boards.donationBoard.configuration.tagSettings = tagSettings;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardPoliciesEnabledChanged = (newValue: boolean) => {
		this.onDataBoardToggleChanged('policiesEnabled', newValue);
	};
	private onDataBoardPoliciesCarrierTagsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				(
					draftAccount.features.boards.policyBoard.configuration.tagSettings as Api.IPolicyDataboardTagSettings
				).carrierTagsEnabled = newValue;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardPoliciesLineOfBusinessTagsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				(
					draftAccount.features.boards.policyBoard.configuration.tagSettings as Api.IPolicyDataboardTagSettings
				).lobTagEnabled = newValue;
			}),
			undefined,
			this.saveAccount
		);
	};
	private onDataBoardMattersEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};

				if (!draftAccount.features.boards.matterBoard && newValue) {
					// Initialize Matter board when enabled for the first time
					draftAccount.features.boards.matterBoard = {
						type: 'Matter',
						enabled: true,
						configuration: {
							tagSettings: {
								_type: 'MatterBoardTagSettings',
								enabled: true,
								matterStageTagEnabled: true,
								practiceAreaTagEnabled: false,
								matterNumberTagEnabled: false,
								courtDateTagEnabled: false,
								statuteOfLimitationsTagEnabled: false,
							} as Api.IMatterDataboardTagSettings,
						},
					};
				} else if (draftAccount.features.boards.matterBoard) {
					// Update existing Matter board
					draftAccount.features.boards.matterBoard.enabled = newValue;
					if (draftAccount.features.boards.matterBoard.configuration?.tagSettings) {
						(
							draftAccount.features.boards.matterBoard.configuration.tagSettings as Api.IMatterDataboardTagSettings
						).enabled = newValue;
					}
				}
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardMeetingsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};

				if (!draftAccount.features.boards.meetingBoard && newValue) {
					draftAccount.features.boards.meetingBoard = {
						type: 'Meeting',
						enabled: true,
						configuration: {},
					};
				} else if (draftAccount.features.boards.meetingBoard) {
					draftAccount.features.boards.meetingBoard.enabled = newValue;
				}
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardServiceJobsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};

				if (!draftAccount.features.boards.serviceJobBoard && newValue) {
					draftAccount.features.boards.serviceJobBoard = {
						type: 'ServiceJob',
						enabled: true,
						configuration: {},
					};
				} else if (draftAccount.features.boards.serviceJobBoard) {
					draftAccount.features.boards.serviceJobBoard.enabled = newValue;
				}
			}),
			undefined,
			this.saveAccount
		);
	};

	private onServiceJobTagSettingsChanged = (settings: Omit<Api.IServiceJobDataboardTagSettings, '_type'>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				if (!draftAccount.features.boards.serviceJobBoard) {
					return;
				}
				draftAccount.features.boards.serviceJobBoard.configuration =
					draftAccount.features.boards.serviceJobBoard.configuration || {};
				const tagSettings = (draftAccount.features.boards.serviceJobBoard.configuration.tagSettings ||
					{}) as Api.IServiceJobDataboardTagSettings;
				tagSettings._type = 'ServiceJobBoardTagSettings';
				tagSettings.enabled = settings.enabled;
				tagSettings.jobStatusTagEnabled = settings.jobStatusTagEnabled;
				tagSettings.lastJobTypeTagEnabled = settings.lastJobTypeTagEnabled;
				tagSettings.lastJobDateTagEnabled = settings.lastJobDateTagEnabled;
				tagSettings.jobInsightTagsEnabled = settings.jobInsightTagsEnabled;
				draftAccount.features.boards.serviceJobBoard.configuration.tagSettings = tagSettings;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardVolunteerEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};

				if (!draftAccount.features.boards.volunteerBoard && newValue) {
					draftAccount.features.boards.volunteerBoard = {
						type: 'Volunteer',
						enabled: true,
						configuration: {},
					};
				} else if (draftAccount.features.boards.volunteerBoard) {
					draftAccount.features.boards.volunteerBoard.enabled = newValue;
				}
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardMattersMatterStageTagsEnabledChanged = (newValue: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.boards = draftAccount.features.boards || {};
				if (draftAccount.features.boards.matterBoard?.configuration?.tagSettings) {
					(
						draftAccount.features.boards.matterBoard.configuration.tagSettings as Api.IMatterDataboardTagSettings
					).matterStageTagEnabled = newValue;
				}
			}),
			undefined,
			this.saveAccount
		);
	};

	private onDataBoardOpportunitiesEnabledChanged = (newValue: boolean) => {
		this.onDataBoardToggleChanged('opportunitiesEnabled', newValue);
	};
	private onLookForLeadsChanged = (option: boolean) => {
		const enabled = option;
		const { account } = this.state;
		this.updateEditableAccount(
			{
				...account,
				features: {
					...account.features,
					emailScan: {
						...account.features.emailScan,
						enabledScanners: enabled ? Api.AllEmailScanners.map(x => x.id) : [],
						saveForAnalysis: option,
					},
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onSaveForAnalysisChanged = () => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.toggleSaveForAnalysis()
			?.then(result => {
				this.updateEmailScanFeatures(result.value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCsmViewMyCampaignsEnabledxError', err);
			});
	};

	private onDefaultContactsVisibilityChanged = (option: ISelectOption<Privacy>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.defaults = draftAccount.preferences.defaults || {};
			draftAccount.preferences.defaults.visibility = option.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onTextingProviderChanged = (option: ISelectOption<ETextingFeatureProvider>) => {
		const textingFeature = this.state.account?.features?.texting || {};
		this.setTextingFeature({
			...textingFeature,
			provider: option.dataContext,
		});
	};

	private onDefaultUserRoleChanged = (option: ISelectOption<UserRole>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.defaults = draftAccount.preferences.defaults || {};
			draftAccount.preferences.defaults.userRole = option.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onDefaultEmailStyleEnabledChanged = (enabled: boolean) => {
		const { savedAccount } = this.state;
		this.setDefaultEmailStyleFeature({
			...(savedAccount?.features?.defaultEmailStyle || {}),
			enabled,
		});
	};

	private onDefaultEmailStyleFontFaceChanged = (fontFace: string) => {
		const { savedAccount } = this.state;
		this.setDefaultEmailStyleFeature({
			...(savedAccount?.features?.defaultEmailStyle || {}),
			fontFace,
		});
	};

	private onDefaultEmailStyleFontSizeChanged = (fontSize: string) => {
		const { savedAccount } = this.state;
		this.setDefaultEmailStyleFeature({
			...(savedAccount?.features?.defaultEmailStyle || {}),
			fontSize,
		});
	};

	private onRequiredRoleToViewAllOpportunities = (option: ISelectOption<UserRole>) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.requiredRoleToViewAllOpportunities = option.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onEnableCcInCampaignsChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.enableCcInCampaigns = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onUseSharedMailboxAsDefaultSendFromChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.useSharedMailboxAsDefaultSendFrom = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSendEmailForNewLeadChanged = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.sendNewLeadEmail = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private sendOnBehalfEnabled = (option: boolean) => {
		const { account } = this.state;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.sendOnBehalfEnabled = option;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSendOnBehalfWaitTimeChanged = (option: ISelectOption<Api.IMeetingDuration>) => {
		const { account } = this.state;
		const durationString = convertDurationToString(option.dataContext);
		this.updateEditableAccount(
			{
				...account,
				preferences: {
					...account.preferences,
					sendOnBehalfWaitTime: durationString,
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onComplianceWaitTimeChanged = (option: ISelectOption<Api.IMeetingDuration>) => {
		const { errorMessages, logApiError } = this.props;
		const durationString = convertDurationToString(option.dataContext);
		this.mAccountOperations
			.updateComplianceWaitTime(durationString)
			?.then(() => {
				this.updateComplianceWaitTime(durationString);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateComplianceWaitTime = (option: string) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.waitTime = option;
			}),
			undefined
		);
	};

	private onConnectionTypesEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;

		this.mAccountOperations
			.updateConnectionTypesFeature({ enabled: option })
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateConnectionTypesFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateConnectionTypesFeaturesError', err);
			});
	};

	private onCrossAccountTemplateSharingChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;

		this.mAccountOperations
			.updateCrossAccountTemplateSharingFeature({ enabled: option })
			?.then(result => {
				if (result.systemCode === 200) {
					this.updateCrossAccountTemplateSharingFeature(result.value);
				} else {
					toast.error(result.systemMessage);
				}
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCrossAccountTemplateSharingFeatureError', err);
			});
	};

	private updateConnectionTypesFeature = (option: Api.IFeature) => {
		const { account } = this.state;

		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.connectionTypes = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private updateCrossAccountTemplateSharingFeature = (option: Api.IFeature) => {
		const { account } = this.state;

		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.crossAccountTemplateSharing = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private toggleAutomations = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.features = draftAccount.features || {};
				draftAccount.features.automation = draftAccount.features.automation || {};
				draftAccount.features.automation.enabled = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private setStartOnBehalfTriggerEnabled = (enabled: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures.allowAdminToStartOnBehalf = enabled;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation.allowAdminToStartOnBehalf = enabled;
					}),
					undefined
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAutomationFeatures-Error', err);
			});
	};

	private onSetShowInternalSendOption = (enabled: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures.showInternalSendOption = enabled;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation.showInternalSendOption = enabled;
					}),
					undefined
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAutomationFeatures-Error', err);
			});
	};

	private onSetAllowStartAutomationsOnHold = (enabled: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures.allowStartAutomationsOnHold = enabled;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation.allowStartAutomationsOnHold = enabled;
					}),
					undefined
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAutomationFeatures-Error', err);
			});
	};

	private onSetAllowAIConditionals = (enabled: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures.allowAIConditionals = enabled;
				})
			)
			?.then(() => {
				const { savedAccount, account: currentAccount } = this.state;
				this.updateEditableAccount(
					produce(currentAccount, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation.allowAIConditionals = enabled;
					}),
					{
						savedAccount: produce(savedAccount, draftAccount => {
							draftAccount.features = draftAccount.features || {};
							draftAccount.features.automation = draftAccount.features.automation || {};
							draftAccount.features.automation.allowAIConditionals = enabled;
						}),
					}
				);
			})
			?.catch(err => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAutomationFeatures-Error', err);
			});
	};

	private setTextingCampaignAutomationTriggerEnabled = (enabled: boolean) => {
		this.setAutomationFeatureEnabledStatus(Api.AutomationTriggerType.Texting, 'additionalAllowedTriggers', enabled);
	};

	private setAutomationFeatureEnabledStatus = (
		feature: Api.AutomationTriggerType,
		automationFeatureField: Extract<
			keyof Api.IAutomationFeatures,
			'additionalAllowedTriggers' | 'additionalAllowedTextStepTriggers'
		>,
		enabled: boolean
	) => {
		const { errorMessages, logApiError } = this.props;
		const { account } = this.state;
		const triggersAsSet = new Set(account?.features?.automation?.[automationFeatureField] || []);
		if (enabled) {
			triggersAsSet.add(feature);
		} else {
			triggersAsSet.delete(feature);
		}
		const triggers = Array.from(triggersAsSet);

		this.mAccountOperations
			.updateAutomationFeatures(
				produce(account.features?.automation ?? {}, draftFeatures => {
					draftFeatures[automationFeatureField] = triggers;
				})
			)
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.features = draftAccount.features || {};
						draftAccount.features.automation = draftAccount.features.automation || {};
						draftAccount.features.automation[automationFeatureField] = triggers;
					}),
					undefined
				);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateImportFromProviderEnabled', err);
			});
	};

	private onDisableMobileAppsChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateDisableMobileApps(value)
			?.then(() => {
				this.updateDisableMobileApps(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateDisableMobileApps', err);
			});
	};

	private onHideCompaniesChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHideCompanies(value)
			?.then(() => {
				this.updateHideCompanies(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHideCompanies', err);
			});
	};

	private onHideContactCompanyEmployeeReportChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHideContactCompanyEmployeeReport(value)
			?.then(() => {
				this.updateHideContactCompanyEmployeeReport(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHideContactCompanyEmployeeReport', err);
			});
	};

	private onHideEmailCampaignSignatureChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHideEmailCampaignSignature(value)
			?.then(() => {
				this.updateHideEmailCampaignSignature(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHideEmailCampaignSignature', err);
			});
	};

	private onShowAllContactsByDefaultChanged = (value: boolean) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updatShowAllContactsByDefault(value)
			?.then(() => {
				this.updateShowAllContactsByDefault(value);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages?.pushApiError(err);
				logApiError?.('UpdateShowAllContactsByDefault', err);
			});
	};

	private updateDisableMobileApps = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.disableMobileApps = value;
			}),
			undefined
		);
	};

	private updateHideCompanies = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.hideCompanies = value;
			}),
			undefined
		);
	};

	private updateHideContactCompanyEmployeeReport = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.hideContactCompanyEmployeeReport = value;
			}),
			undefined
		);
	};

	private updateHideEmailCampaignSignature = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.hideEmailCampaignSignature = value;
			}),
			undefined
		);
	};

	private updateShowAllContactsByDefault = (value: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account!, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.showAllContactsByDefault = value;
			}),
			undefined
		);
	};

	private onSetRenewalReminderDaysOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.renewalEmailDaysToLookAhead = days.dataContext || 0;
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.PolicyRenew =
				draftAccount.preferences.resourceSelectorSettings.PolicyRenew || {};
			draftAccount.preferences.resourceSelectorSettings.PolicyRenew.daysToLookAhead = days.dataContext || 0;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetRenewalReminderDaysOptionChange = (option: boolean) => {
		const { account } = this.state;
		const days = option ? 30 : 0;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.renewalEmailDaysToLookAhead = days;
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.PolicyRenew =
				draftAccount.preferences.resourceSelectorSettings.PolicyRenew || {};
			draftAccount.preferences.resourceSelectorSettings.PolicyRenew.daysToLookAhead = days;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning65MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.Turning65 =
				draftAccount.preferences.resourceSelectorSettings.Turning65 || {};
			draftAccount.preferences.resourceSelectorSettings.Turning65.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning65MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.Turning65 =
					draftAccount.preferences.resourceSelectorSettings.Turning65 || {};
				draftAccount.preferences.resourceSelectorSettings.Turning65.daysToLookAhead = option ? 60 : 0;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXXMonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX =
				draftAccount.preferences.resourceSelectorSettings.TurningXX || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXXMonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX =
					draftAccount.preferences.resourceSelectorSettings.TurningXX || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX2MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX2 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX2 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX2.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX2._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX2MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX2 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX2 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX2.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX2._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX3MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX3 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX3 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX3.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX3._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX3MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX3 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX3 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX3.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX3._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX4MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX4 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX4 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX4.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX4._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX4MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX4 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX4 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX4.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX4._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurningXX5MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.TurningXX5 =
				draftAccount.preferences.resourceSelectorSettings.TurningXX5 || {};
			draftAccount.preferences.resourceSelectorSettings.TurningXX5.daysToLookAhead = days.dataContext;
			draftAccount.preferences.resourceSelectorSettings.TurningXX5._type = 'TurningXXResourceSelectorSettings';
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurningXX5MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.TurningXX5 =
					draftAccount.preferences.resourceSelectorSettings.TurningXX5 || {};
				draftAccount.preferences.resourceSelectorSettings.TurningXX5.daysToLookAhead = option ? 60 : 0;
				draftAccount.preferences.resourceSelectorSettings.TurningXX5._type = 'TurningXXResourceSelectorSettings';
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetFinancialReviewOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.FinancialReview =
				draftAccount.preferences.resourceSelectorSettings.FinancialReview || {};
			draftAccount.preferences.resourceSelectorSettings.FinancialReview.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetFinancialReviewOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				if (!option) {
					delete draftAccount.preferences.resourceSelectorSettings.FinancialReview;
					return;
				}
				draftAccount.preferences.resourceSelectorSettings.FinancialReview =
					draftAccount.preferences.resourceSelectorSettings.FinancialReview || {};
				draftAccount.preferences.resourceSelectorSettings.FinancialReview.daysToLookAhead = 60;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurning72MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.Turning72 =
				draftAccount.preferences.resourceSelectorSettings.Turning72 || {};
			draftAccount.preferences.resourceSelectorSettings.Turning72.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning72MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.Turning72 =
					draftAccount.preferences.resourceSelectorSettings.Turning72 || {};
				draftAccount.preferences.resourceSelectorSettings.Turning72.daysToLookAhead = option ? 60 : 0;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetTurning73MonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		const days = option;
		const newAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings.Turning73 =
				draftAccount.preferences.resourceSelectorSettings.Turning73 || {};
			draftAccount.preferences.resourceSelectorSettings.Turning73.daysToLookAhead = days.dataContext;
		});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetTurning73MonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				draftAccount.preferences.resourceSelectorSettings.Turning73 =
					draftAccount.preferences.resourceSelectorSettings.Turning73 || {};
				draftAccount.preferences.resourceSelectorSettings.Turning73.daysToLookAhead = option ? 60 : 0;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetNewDonationsOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const newDonations: Api.IActionableResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.NewDonations || {};
				newDonations._type = 'ActionableResourceSelectorSettings';
				newDonations.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.NewDonations = newDonations;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetNewPolicyOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const newPolicy: Api.INewPolicyActionableResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.NewPolicy || {};
				newPolicy._type = 'NewPolicyActionableResourceSelectorSettings';
				newPolicy.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.NewPolicy = newPolicy;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetNewMatterOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const newMatter: Api.IActionableResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.NewMatter || {};
				newMatter._type = 'ActionableResourceSelectorSettings';
				newMatter.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.NewMatter = newMatter;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetMatterStageChangedOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const matterStageChanged: Api.IActionableResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.MatterStageChanged || {};
				matterStageChanged._type = 'ActionableResourceSelectorSettings';
				matterStageChanged.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.MatterStageChanged = matterStageChanged;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetMatterStatusChangedOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const matterStatusChanged: Api.IActionableResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.MatterStatusChanged || {};
				matterStatusChanged._type = 'ActionableResourceSelectorSettings';
				matterStatusChanged.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.MatterStatusChanged = matterStatusChanged;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetServiceJobCompletedOptionChange = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const serviceJobCompleted: Api.IServiceJobCompletedResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.ServiceJobCompleted || {};
				// Not the shared actionable type: the API's up-convert off it rebuilds the entry without minimumTotal.
				serviceJobCompleted._type = 'ServiceJobCompletedResourceSelectorSettings';
				serviceJobCompleted.enabled = option;
				draftAccount.preferences.resourceSelectorSettings.ServiceJobCompleted = serviceJobCompleted;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetServiceJobCompletedMinimumTotal = (value?: number) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);

				const serviceJobCompleted: Api.IServiceJobCompletedResourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings.ServiceJobCompleted || {};
				// An omitted _type drops the whole entry, enabled toggle included — mutate the loaded object, never rebuild.
				serviceJobCompleted._type = 'ServiceJobCompletedResourceSelectorSettings';
				serviceJobCompleted.minimumTotal = value;
				draftAccount.preferences.resourceSelectorSettings.ServiceJobCompleted = serviceJobCompleted;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onAddCustomField = () => {
		const account: Api.IAccount = { ...this.state.account };
		account.additionalInfo = account.additionalInfo || {};
		account.additionalInfo.hasCustomContactFields = true;
		this.updateEditableAccount(account);
	};

	private onAddCustomCard = (values: ISubmittedValues) => {
		const { account } = this.state;
		const NUM_OF_CUSTOM_RESOURCES = 10;
		let newCustomCard: Api.IAccount;
		const accountSettings = account.preferences?.resourceSelectorSettings;

		for (let i = 1; i <= NUM_OF_CUSTOM_RESOURCES; i++) {
			const cardKey = `Custom${i}` as keyof typeof accountSettings;
			if (!accountSettings?.[cardKey]) {
				const customSetting = `Custom${i}` as TCustomSettings;
				newCustomCard = produce(account, this.assignDraft(customSetting, values));
				break;
			}
		}

		this.updateEditableAccount(newCustomCard, undefined, this.saveAccount);
	};

	private onBlogEnabledChanged = (option: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateBlogEnabled(enabled)
			?.then(() => {
				this.updateEditableAccount(account, undefined, this.saveAccount);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAnyUserEditingNotesError', err);
			});
	};

	private onHIPAAEnabledChanged = (option: boolean) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateHIPAAEnabled(enabled)
			?.then(() => {
				this.updateEditableAccount(account, undefined, this.saveAccount);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateHIPAAEnabledError', err);
			});
	};

	private onUpdateCustomCard = (values: ISubmittedValues, card: TCustomSettings) => {
		const { account } = this.state;
		const newCustomCard: Api.IAccount = produce(account, this.assignDraft(card, values));

		this.updateEditableAccount(newCustomCard, undefined, this.saveAccount);
	};

	private onUpdateCustomCardDays = (value: ISelectOption<number>, card: TCustomSettings) => {
		const { account } = this.state;
		const newCustomCard: Api.IAccount = produce(account, draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings[card] =
				draftAccount.preferences.resourceSelectorSettings[card] || {};

			draftAccount.preferences.resourceSelectorSettings[card].daysToLookAhead = value.dataContext;
		});

		this.updateEditableAccount(newCustomCard, undefined, this.saveAccount);
	};

	private assignDraft = (
		card: TCustomSettings,
		values: ISubmittedValues
	): ((draft: WritableDraft<Api.IAccount>) => void | WritableDraft<Api.IAccount>) => {
		return draftAccount => {
			draftAccount.preferences = draftAccount.preferences || {};
			draftAccount.preferences.resourceSelectorSettings =
				draftAccount.preferences.resourceSelectorSettings || ({} as any);
			draftAccount.preferences.resourceSelectorSettings[card] =
				draftAccount.preferences.resourceSelectorSettings[card] || {};
			draftAccount.preferences.resourceSelectorSettings[card].displayName = values.displayName;
			draftAccount.preferences.resourceSelectorSettings[card].daysToLookAhead = values.daysToLookAhead;
			draftAccount.preferences.resourceSelectorSettings[card].phrases = values.phrases;
			draftAccount.preferences.resourceSelectorSettings[card]._type = 'CustomResourceSelectorSettings';
		};
	};

	private onSuppressCustomCardOptionChange = (card: TCustomSettings) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.resourceSelectorSettings =
					draftAccount.preferences.resourceSelectorSettings || ({} as any);
				delete draftAccount.preferences.resourceSelectorSettings[card];
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetRenewalSuppressMonthsOption = (option: ISelectOption<number>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.renewalEmailMonthsToSuppress = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetRenewalSuppressMonthsOptionChange = (option: boolean) => {
		const { account } = this.state;
		const newAccount = option
			? produce(account, draftAccount => {
					draftAccount.preferences = draftAccount.preferences || {};
					draftAccount.preferences.renewalEmailMonthsToSuppress = 6;
				})
			: produce(account, draftAccount => {
					draftAccount.preferences = draftAccount.preferences || {};
					draftAccount.preferences.renewalEmailMonthsToSuppress = 0;
				});
		this.updateEditableAccount(newAccount, undefined, this.saveAccount);
	};

	private onSetSuppressContactsRemovedFromResourceSends = (option: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.suppressContactsRemovedFromResourceSends = option;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetShowUpcomingBirthdaysToOption = (option: ISelectOption<Api.UpcomingKeyDateDisplayScope>) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.showUpcomingBirthdaysTo = option.dataContext;
			}),
			undefined,
			this.saveAccount
		);
	};

	private onSetEnhancementOption = (option: ISelectOption<EnhancementType>) => {
		const { account } = this.state;
		const preferences: Partial<Api.IAccountPreferences> = {};

		switch (option.dataContext) {
			case 'off':
				preferences.enableCompanyEnhancement = false;
				break;
			case 'companiesOnly':
				preferences.enableCompanyEnhancement = true;
				break;
			default:
				return;
		}

		this.updateEditableAccount(
			{
				...account,
				preferences: {
					...account.preferences,
					...preferences,
				},
			},
			undefined,
			this.saveAccount
		);
	};

	private onClickAccountFlagged = () => {
		this.mAccountOperations?.updateAccountFlagged()?.catch((err: Api.IOperationResultNoValue) => {
			const { errorMessages, logApiError } = this.props;
			errorMessages.pushApiError(err);
			logApiError('UpdateAccountFlagged-Error', err);
		});
	};

	private onTwoFactorAuthChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.toggleTwoFactorAuth(enabled)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Turning off second factor authentication requires an account maintenance ticket.'],
				});
				logApiError('UpdateTwoFactorAuthError', err);
			});
	};

	private onAllowOAuthLoginChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const account = this.mAccountOperations.account;
		const oauthLoginSettings: Api.IOAuthLoginSettings = {
			allowOAuthLogin: option,
			// If we are disabling OAuth login, we can't require it. So turn it off.
			onlyAllowOAuthLogin: option ? account?.oAuthLoginSettings?.onlyAllowOAuthLogin : false,
		};
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateOAuthLoginSettings(oauthLoginSettings)
			?.then(() => {
				this.updateEditableAccount(this.mAccountOperations.account, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Failed to update OAuth login setting.'],
				});
				logApiError('UpdateOAuthLoginSettingsError', err);
			});
	};

	private onOnlyAllowOAuthLoginChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const oauthLoginSettings: Api.IOAuthLoginSettings = {
			onlyAllowOAuthLogin: option,
		};
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateOAuthLoginSettings(oauthLoginSettings)
			?.then(() => {
				this.updateEditableAccount(this.mAccountOperations.account, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Failed to update only allow OAuth login setting.'],
				});
				logApiError('UpdateOAuthLoginSettingsError', err);
			});
	};

	private onEnabledOAuthProvidersChanged = (providers: string[]) => {
		const { errorMessages, logApiError } = this.props;
		const oauthLoginSettings: Api.IOAuthLoginSettings = {
			enabledProviders: providers,
		};
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateOAuthLoginSettings(oauthLoginSettings)
			?.then(() => {
				this.updateEditableAccount(this.mAccountOperations.account, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.push({
					messages: ['Failed to update enabled OAuth providers.'],
				});
				logApiError('UpdateOAuthLoginSettingsError', err);
			});
	};

	// Not updateWebsitesFeature -- that endpoint only persists the Duda site id / website url.
	private onWebsitesEnablementChanged = (enabled: boolean, tier?: WebsiteTier) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updateWebsitesEnablement({ enabled, tier })
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateWebsitesEnablementError', err);
			});
	};

	private onWebsitesStringValueChanged = (value: string, key: StringKeys<Api.IWebsitesFeature>) => {
		const websiteFeature = { ...this.mAccountOperations.account.features.websites };
		websiteFeature[key] = value;

		this.onWebsitesValueChanged(websiteFeature);
	};

	private onWebsitesValueChanged = (websiteFeature: Api.IWebsitesFeature) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updateWebsitesFeature(websiteFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateWebsitesWebsiteUrlError', err);
			});
	};

	private onSetNoteViewerCanEditEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateAnyUserEditingNotes(enabled)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateAnyUserEditingNotesError', err);
			});
	};

	private onToggleAccountPreferenceBoolean = (path: BooleanKeys<Api.IAccountPreferences>, option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations
			.toggleAccountPreference(path, enabled)
			?.then(() => {
				this.updateAccountPreferenceBoolean(path, enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError(`UpdateAccountPreferenceBoolean-${path}`, err);
			});
	};

	private onRequiredRoleForFullLevitateUI = (option: ISelectOption<UserRole>) => {
		const { account } = this.state;
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateAccountPreferenceString('requiredRoleForFullLevitateUI', option.dataContext.toString())
			?.then(() => {
				this.updateEditableAccount(
					produce(account, draftAccount => {
						draftAccount.preferences = draftAccount.preferences || {};
						draftAccount.preferences.requiredRoleForFullLevitateUI = option.dataContext;
					}),
					undefined
				);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError(`UpdateAccountPreferenceBoolean-requiredRoleForFullLevitateUI`, err);
			});
	};

	private updateAccountPreferenceBoolean = (path: BooleanKeys<Api.IAccountPreferences>, enabled: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences[path] = enabled;
			}),
			undefined
		);
	};

	private onComplianceEnabledChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateComplianceEnabled(enabled)
			?.then(() => {
				this.updateComplianceEnabled(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateComplianceEnabled = (enabled: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.enabled = enabled;
			}),
			undefined
		);
	};

	private onSendComplianceReviewResultEmailsChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateSendComplianceReviewResultEmails(enabled)
			?.then(() => {
				this.updateSendComplianceReviewResultEmails(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateSendComplianceReviewResultEmails = (enabled: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.sendComplianceReviewResultEmails = enabled;
			}),
			undefined
		);
	};

	private onComplianceEmailAddressChanged = (emailAddress: string) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		return this.mAccountOperations
			.updateComplianceEmailAddress(emailAddress)
			?.then(() => {
				this.updateComplianceEmailAddress(emailAddress);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateComplianceEmailAddress = (emailAddress: string) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.emailAddress = emailAddress;
			}),
			undefined
		);
	};

	private onComplianceRequiredChanged = (option: boolean) => {
		const { errorMessages, logApiError } = this.props;
		const enabled = option;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});
		this.mAccountOperations
			.updateComplianceRequired(enabled)
			?.then(() => {
				this.updateComplianceRequired(enabled);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateComplianceError', err);
			});
	};

	private updateComplianceRequired = (enabled: boolean) => {
		const { account } = this.state;
		this.updateEditableAccount(
			produce(account, draftAccount => {
				draftAccount.preferences = draftAccount.preferences || {};
				draftAccount.preferences.complianceSettings = draftAccount.preferences.complianceSettings || {};
				draftAccount.preferences.complianceSettings.required = enabled;
			}),
			undefined
		);
	};

	private onUpdatePreApprovedFeature = (option: boolean) => {
		const preApprovedFeature = { ...this.state.account.features.templateReviewSettings?.preApproved };
		preApprovedFeature.enabled = option;

		this.onPreApprovedValueChanged(preApprovedFeature);
	};

	private onUpdatePreApprovedDisplayText = (value: string) => {
		const preApprovedFeature = { ...this.state.account.features.templateReviewSettings?.preApproved };
		preApprovedFeature.displayText = value;

		this.onPreApprovedValueChanged(preApprovedFeature);
	};

	private onUpdatePreApprovedEmailPrefix = (value: string) => {
		const preApprovedFeature = { ...this.state.account.features.templateReviewSettings?.preApproved };
		preApprovedFeature.complianceEmailSubjectPrefix = value;

		this.onPreApprovedValueChanged(preApprovedFeature);
	};

	private onPreApprovedValueChanged = (preApprovedFeature: Api.ICampaignTemplateReviewSettings) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updatePreApprovedFeature(preApprovedFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdatePreApprovedFeatureError', err);
			});
	};

	private onUpdatePreReviewedFeature = (option: boolean) => {
		const preReviewedFeature = { ...this.state.account.features.templateReviewSettings?.preReviewed };
		preReviewedFeature.enabled = option;

		this.onPreReviewedValueChanged(preReviewedFeature);
	};

	private onUpdatePreReviewedDisplayText = (value: string) => {
		const preReviewedFeature = { ...this.state.account.features.templateReviewSettings?.preReviewed };
		preReviewedFeature.displayText = value;

		this.onPreReviewedValueChanged(preReviewedFeature);
	};

	private onUpdatePreReviewedEmailPrefix = (value: string) => {
		const preReviewedFeature = { ...this.state.account.features.templateReviewSettings?.preReviewed };
		preReviewedFeature.complianceEmailSubjectPrefix = value;

		this.onPreReviewedValueChanged(preReviewedFeature);
	};

	private onPreReviewedValueChanged = (preReviewedFeature: Api.ICampaignTemplateReviewSettings) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updatePreReviewedFeature(preReviewedFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdatePreReviewedFeatureError', err);
			});
	};
	private onUpdateRejectedFeature = (option: boolean) => {
		const rejectedFeature = { ...this.state.account.features.templateReviewSettings.rejected };
		rejectedFeature.enabled = option;

		this.onRejectedValueChanged(rejectedFeature);
	};

	private onUpdateRejectedDisplayText = (value: string) => {
		const rejectedFeature = { ...this.state.account.features.templateReviewSettings.rejected };
		rejectedFeature.displayText = value;

		this.onRejectedValueChanged(rejectedFeature);
	};

	private onUpdateRejectedEmailPrefix = (value: string) => {
		const rejectedFeature = { ...this.state.account.features.templateReviewSettings.rejected };
		rejectedFeature.complianceEmailSubjectPrefix = value;

		this.onRejectedValueChanged(rejectedFeature);
	};

	private onRejectedValueChanged = (rejectedFeature: Api.ICampaignTemplateReviewSettings) => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations.impersonate({
			account: { id: this.mAccountOperations.account.id },
		});

		this.mAccountOperations
			.updateRejectedFeature(rejectedFeature)
			?.then(res => {
				this.updateEditableAccount(res.value, undefined);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateRejectedFeatureError', err);
			});
	};

	// Custom Calendar Event Handlers
	private onCustomCalendarEditToggle = () => {
		this.setState({
			isCustomCalendarEditMode: !this.state.isCustomCalendarEditMode,
		});
	};

	private onCustomCalendarStatusChanged = (option: ISelectBoxOption<string>): void => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateCustomCalendarStatus(option.value)
			?.then(result => {
				this.updateCustomCalendarFeature(result);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarStatus-Error', err);
			});
	};

	private onCustomCalendarIntakeFormDueChanged = (dateInput: string): void => {
		const { errorMessages, logApiError } = this.props;

		// If empty, clear the date
		if (!dateInput.trim()) {
			this.mAccountOperations
				.updateCustomCalendarIntakeFormDue(null)
				?.then(result => {
					this.updateCustomCalendarFeature(result);
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
					logApiError('UpdateCustomCalendarIntakeFormDue-Error', err);
				});
			return;
		}

		// Parse date from YYYY-MM-DD format (from DateInput)
		const parsedDate = moment(dateInput, 'YYYY-MM-DD', true);

		if (!parsedDate.isValid()) {
			toast.error('Please enter a valid date in YYYY-MM-DD format');
			return;
		}

		// Convert to ISO string for API
		const isoDateString = parsedDate.toISOString();

		this.mAccountOperations
			.updateCustomCalendarIntakeFormDue(isoDateString)
			?.then(result => {
				this.updateCustomCalendarFeature(result);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarIntakeFormDue-Error', err);
			});
	};

	private onEstimatedDeliveryDateChanged = (dateValue: string): void => {
		const { errorMessages, logApiError } = this.props;

		if (!dateValue.trim()) {
			this.mAccountOperations
				.updateCustomCalendarEstimatedDeliveryDate(null)
				?.then(result => this.updateCustomCalendarFeature(result))
				?.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
					logApiError('UpdateCustomCalendarEstimatedDeliveryDate-Error', err);
				});
			return;
		}

		const parsedDate = moment(dateValue, 'YYYY-MM-DD', true);
		if (!parsedDate.isValid()) {
			toast.error('Please enter a valid date in YYYY-MM-DD format');
			return;
		}

		this.mAccountOperations
			.updateCustomCalendarEstimatedDeliveryDate(parsedDate.toISOString())
			?.then(result => this.updateCustomCalendarFeature(result))
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarEstimatedDeliveryDate-Error', err);
			});
	};

	private onCustomCalendarModificationsChanged = (option: ISelectBoxOption<string>): void => {
		const { errorMessages, logApiError } = this.props;
		this.mAccountOperations
			.updateCustomCalendarModifications(option.value)
			?.then(result => {
				this.updateCustomCalendarFeature(result);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
				logApiError('UpdateCustomCalendarModifications-Error', err);
			});
	};
}

const ObservableAccountDetails = observer(_AccountDetails);
const AccountDetailsWithContext = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey,
	FullScreenModalViewModelKey
)(ObservableAccountDetails);
export const AccountDetails = withRouter(withEventLogging(AccountDetailsWithContext, 'AccountDetails'));
